import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  ListSubheader,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { fetchLanes } from '@src/api/actions/dashboard/lanes';
import { fetchVerticals } from '@src/api/actions/verticals';
import { fetchClientOffers } from '@src/api/actions/dashboard/clientOffers';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import BriefMarkdownEditor from '@src/components/creative/BriefMarkdownEditor';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LaunchIcon from '@mui/icons-material/Launch';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  fetchActiveConstitution,
  updateConstitution,
  listAdConcepts,
  fetchAdConcept,
  listLaneBriefs,
  fetchLaneBrief,
  updateLaneBrief,
  generateFlight,
  rejudgeLaneBrief,
  markLaneBriefLaunched,
  assignLaneBrief,
  unassignLaneBrief,
  deployLaneBrief,
  listBriefShots,
  setBriefShotSource,
  listDesignerProfiles,
  listDesignerGroups,
  deleteLaneBrief,
  listWinnerLibrary,
  createWinnerLibraryEntry,
  updateWinnerLibraryEntry,
  deleteWinnerLibraryEntry,
  proposeCells,
  acceptCellProposal,
  auditConstitution,
  applyConstitutionEdit,
  renderLaneBrief,
  listRenderedAssets,
  recomposeLaneBrief,
  addBangerFromDecomposition,
  listAdInspirations,
  createAdInspiration,
  reanalyzeAdInspiration,
  generateBriefFromCell,
  createManualBrief,
} from '@src/api/creativeStudio';

// apiClient.request resolves with the full axios response — unwrap consistently.
const unwrap = (res) => res?.data ?? res;
const asArray = (res) => {
  const d = unwrap(res);
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.results)) return d.results;
  return [];
};

// Styled, designer-first rendering of the brief markdown. System/QA meta
// (everything from the "## System details" marker on) is split into a
// collapsed panel so the designer only sees what they need to make the ad.
const MD_SX = {
  '& h1': { fontSize: '1.35rem', fontWeight: 700, mt: 0, mb: 1 },
  '& h2': { fontSize: '1.05rem', fontWeight: 700, mt: 2.5, mb: 1 },
  '& p': { my: 0.75, lineHeight: 1.5 },
  '& ul': { my: 0.5, pl: 3 },
  '& li': { my: 0.25 },
  '& table': { borderCollapse: 'collapse', width: '100%', my: 1, fontSize: '0.85rem' },
  '& th, & td': { border: '1px solid', borderColor: 'divider', p: 0.75, textAlign: 'left', verticalAlign: 'top' },
  '& th': { bgcolor: 'grey.100', fontWeight: 700 },
  '& code': { bgcolor: 'grey.100', px: 0.5, borderRadius: 0.5, fontSize: '0.85em' },
  '& hr': { border: 0, borderTop: '1px solid', borderColor: 'divider', my: 2 },
};
function DesignerBriefView({ markdown }) {
  if (!markdown) return <Typography variant="body2" color="text.secondary">(empty)</Typography>;
  const marker = '\n## System details';
  const i = markdown.indexOf(marker);
  const main = i === -1 ? markdown : markdown.slice(0, i);
  const sys = i === -1 ? '' : markdown.slice(i + 1); // keep the heading
  return (
    <>
      <Box sx={MD_SX}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{main}</ReactMarkdown>
      </Box>
      {sys && (
        <Accordion disableGutters elevation={0} sx={{ mt: 2, border: '1px solid', borderColor: 'divider' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="caption" color="text.secondary">
              System details (QA / engine — not needed to make the ad)
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ ...MD_SX, '& h2': { display: 'none' } }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{sys}</ReactMarkdown>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Top-level tabbed page
// ---------------------------------------------------------------------------

const TAB_LIBRARY = 'library';
const TAB_BRIEFS = 'briefs';
const TAB_WINNERS = 'winners';
const TAB_ADD_BANGER = 'add_banger';
const TAB_CONSTITUTION = 'constitution';

function ConceptSystemPage() {
  const [tab, setTab] = useState(TAB_LIBRARY);

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h4">Creative Engine</Typography>
          <Chip label="v1" color="primary" size="small" />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Brief generation anchored on banger-decomposed cells, scored by the deterministic Judge,
          deployed across lanes via per-client substitution. Replaces the avatar × pain-point system.
        </Typography>

        <Paper sx={{ mt: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            indicatorColor="primary"
            textColor="primary"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value={TAB_LIBRARY} label="Concept Library" />
            <Tab value={TAB_BRIEFS} label="Briefs" />
            <Tab value={TAB_WINNERS} label="Winner Library" />
            <Tab value={TAB_ADD_BANGER} label="Add Banger" />
            <Tab value={TAB_CONSTITUTION} label="Constitution" />
          </Tabs>
          <Box sx={{ p: 3 }}>
            {tab === TAB_LIBRARY && <LibraryTab />}
            {tab === TAB_BRIEFS && <BriefsTab />}
            {tab === TAB_WINNERS && <WinnersTab />}
            {tab === TAB_ADD_BANGER && <AddBangerTab />}
            {tab === TAB_CONSTITUTION && <ConstitutionTab />}
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
}

export default ConceptSystemPage;

// ---------------------------------------------------------------------------
// LIBRARY TAB — concepts + nested cells
// ---------------------------------------------------------------------------

function LibraryTab() {
  const [concepts, setConcepts] = useState([]);
  const [conceptDetails, setConceptDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [proposeFor, setProposeFor] = useState(null); // concept slug

  const refreshConcepts = useCallback(() => {
    listAdConcepts()
      .then((res) => setConcepts(asArray(res)))
      .catch((e) => setError(String(e)));
  }, []);

  useEffect(() => {
    refreshConcepts();
    setLoading(false);
  }, [refreshConcepts]);

  const refreshConceptDetail = useCallback(async (slug) => {
    try {
      const res = await fetchAdConcept(slug);
      setConceptDetails((prev) => ({ ...prev, [slug]: unwrap(res) }));
    } catch (e) {
      setError(String(e));
    }
  }, []);

  const handleExpand = useCallback(
    async (slug) => {
      const isOpen = expanded === slug;
      setExpanded(isOpen ? null : slug);
      if (!isOpen && !conceptDetails[slug]) {
        await refreshConceptDetail(slug);
      }
    },
    [expanded, conceptDetails, refreshConceptDetail],
  );

  if (loading) return <LinearProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {concepts.length} active concept(s). Click to expand and see the canonical + sibling cells.
        Cells are managed in Django admin; their axis coordinates drive brief generation.
      </Typography>
      {concepts.map((c) => (
        <Accordion
          key={c.id}
          expanded={expanded === c.slug}
          onChange={() => handleExpand(c.slug)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
              <Typography variant="h6" sx={{ minWidth: 160 }}>{c.name}</Typography>
              <Chip
                size="small"
                label={c.concept_class}
                color={c.concept_class === 'authenticity' ? 'warning' : 'primary'}
                variant="outlined"
              />
              <Chip size="small" label={`priority ${c.priority}`} variant="outlined" />
              <Chip size="small" label={`${c.cell_count} cells`} />
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1, ml: 2 }}>
                {c.canonical_description}
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                {conceptDetails[c.slug]?.cells?.length || 0} cells. Click "Propose new" to ask Claude for sibling-cell ideas.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AutoFixHighIcon />}
                onClick={() => setProposeFor(c.slug)}
              >
                Propose new cells
              </Button>
            </Stack>
            <CellsTable cells={conceptDetails[c.slug]?.cells || []} />
            {conceptDetails[c.slug] && (
              <Stack spacing={1} sx={{ mt: 2 }}>
                <Typography variant="caption" fontWeight={600}>Offer-justification logic:</Typography>
                <Typography variant="caption" color="text.secondary">
                  {conceptDetails[c.slug].offer_justification_logic}
                </Typography>
                <Typography variant="caption" fontWeight={600}>When to use:</Typography>
                <Typography variant="caption" color="text.secondary">
                  {conceptDetails[c.slug].when_to_use}
                </Typography>
                <Typography variant="caption" fontWeight={600}>Production cautions:</Typography>
                <Typography variant="caption" color="text.secondary">
                  {conceptDetails[c.slug].production_cautions}
                </Typography>
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
      ))}

      {proposeFor && (
        <ProposeCellsDialog
          conceptSlug={proposeFor}
          conceptName={concepts.find((c) => c.slug === proposeFor)?.name || proposeFor}
          onClose={() => setProposeFor(null)}
          onAccepted={() => refreshConceptDetail(proposeFor)}
        />
      )}
    </Stack>
  );
}

function ProposeCellsDialog({ conceptSlug, conceptName, onClose, onAccepted }) {
  const [count, setCount] = useState(3);
  const [steering, setSteering] = useState('');
  const [proposals, setProposals] = useState(null); // null = not run yet; [] = empty result
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [acceptedSlugs, setAcceptedSlugs] = useState(new Set());
  const [acceptingSlug, setAcceptingSlug] = useState(null);

  const handleRun = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    setProposals(null);
    setAcceptedSlugs(new Set());
    try {
      const r = await proposeCells(conceptSlug, { count: Number(count), steering });
      const data = unwrap(r);
      setProposals(Array.isArray(data?.proposals) ? data.proposals : []);
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally {
      setSubmitting(false);
    }
  }, [conceptSlug, count, steering]);

  const handleAccept = useCallback(
    async (proposal) => {
      setAcceptingSlug(proposal.slug);
      setError(null);
      try {
        await acceptCellProposal(conceptSlug, proposal);
        setAcceptedSlugs((s) => new Set([...s, proposal.slug]));
        onAccepted?.();
      } catch (e) {
        setError(e?.response?.data?.detail || String(e));
      } finally {
        setAcceptingSlug(null);
      }
    },
    [conceptSlug, onAccepted],
  );

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Propose new cells for <strong>{conceptName}</strong>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Claude reads this concept's existing cells + active bangers + Constitution and proposes
            sibling cells that fill gaps in the library. Each proposal differs on 3+ axes from
            every existing cell. <strong>Costs ~$0.30 per run.</strong>
          </Alert>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="How many proposals?"
              type="number"
              value={count}
              onChange={(e) => setCount(e.target.value)}
              inputProps={{ min: 1, max: 8 }}
              size="small"
              sx={{ width: 180 }}
            />
            <TextField
              label="Optional steering"
              value={steering}
              onChange={(e) => setSteering(e.target.value)}
              size="small"
              placeholder='e.g. "focus on low-cost graphics-driven variants"'
              fullWidth
            />
            <Button
              variant="contained"
              startIcon={submitting ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
              onClick={handleRun}
              disabled={submitting}
            >
              {submitting ? 'Generating…' : 'Propose'}
            </Button>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}

          {proposals && proposals.length === 0 && (
            <Alert severity="warning">Claude returned no proposals (likely all duplicated existing cells).</Alert>
          )}

          {proposals && proposals.length > 0 && (
            <Stack spacing={1.5}>
              <Typography variant="caption" color="text.secondary">
                {proposals.length} proposal{proposals.length === 1 ? '' : 's'}. Click ✓ to add as a non-canonical cell.
              </Typography>
              {proposals.map((p) => {
                const accepted = acceptedSlugs.has(p.slug);
                const accepting = acceptingSlug === p.slug;
                return (
                  <Card key={p.slug} variant="outlined">
                    <CardContent>
                      <Stack direction="row" alignItems="flex-start" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600}>{p.name}</Typography>
                            <Chip size="small" label={p.slug} variant="outlined" />
                            <Chip size="small" label={p.estimated_cost_tier || 'medium'} />
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            {p.what_makes_it_work}
                          </Typography>
                          <Grid container spacing={1}>
                            {p.axes && Object.entries(p.axes).map(([k, v]) => (
                              <Grid item xs={12} sm={6} key={k}>
                                <Typography variant="caption" fontWeight={600}>{k}:</Typography>
                                <Typography variant="caption" sx={{ ml: 1 }}>{String(v)}</Typography>
                              </Grid>
                            ))}
                          </Grid>
                          {p.hook_pattern && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                              Hook: "{p.hook_pattern}"
                            </Typography>
                          )}
                          {p.reasoning_for_proposal && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                              <strong>Why:</strong> {p.reasoning_for_proposal}
                            </Typography>
                          )}
                          {p.andromeda_distance_check && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                              <strong>Andromeda:</strong> min distance to existing ≈ {p.andromeda_distance_check.min_distance_to_existing}, differs from canonical on {(p.andromeda_distance_check.differs_from_canonical_on || []).join(', ')}
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="column" spacing={1} alignItems="flex-end">
                          {accepted ? (
                            <Chip
                              size="small"
                              color="success"
                              icon={<CheckCircleIcon />}
                              label="Added"
                            />
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={accepting ? <CircularProgress size={14} /> : <CheckCircleIcon />}
                              disabled={accepting}
                              onClick={() => handleAccept(p)}
                            >
                              {accepting ? 'Adding…' : 'Accept'}
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{acceptedSlugs.size > 0 ? 'Close' : 'Cancel'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// Pick a lane → generate ONE brief from a specific cell (skips the composer).
function GenerateFromCellDialog({ cell, onClose }) {
  const [lanes, setLanes] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [laneId, setLaneId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchLanes().then((r) => setLanes(asArray(r))).catch(() => {});
    fetchVerticals().then((r) => setVerticals(asArray(r))).catch(() => {});
  }, []);

  const vName = (vid) => {
    const v = verticals.find((x) => String(x.id) === String(vid));
    return v ? (v.vertical || v.name || '') : '';
  };
  const selectedLane = lanes.find((l) => String(l.id) === String(laneId)) || null;

  const submit = async () => {
    if (!laneId) return;
    setBusy(true); setError(null);
    try {
      const r = await generateBriefFromCell({ cellId: cell.id, laneId: Number(laneId) });
      setResult(unwrap(r));
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate brief from cell</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info" variant="outlined">
            Generates one brief from <strong>{cell.name}</strong> exactly — bypasses the
            auto-composer. One Claude call (~$0.30).
          </Alert>
          {error && <Alert severity="error">{error}</Alert>}
          {!result ? (
            <Autocomplete
              size="small"
              options={lanes}
              getOptionLabel={(l) => (l
                ? `${l.name}${vName(l.vertical_id) ? ` · ${vName(l.vertical_id)}` : ''}`
                : '')}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
              value={selectedLane}
              onChange={(_e, v) => setLaneId(v?.id ? String(v.id) : '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Lane"
                  placeholder="Pick a lane…"
                  required
                  helperText="Vertical + a representative offer are derived from the lane."
                />
              )}
            />
          ) : (
            <Alert severity="success">
              Created <strong>{result.brief_code}</strong>
              {result.judge_score != null ? ` · judge ${result.judge_score.toFixed(2)}` : ''}
              {' '}— find it in the Briefs tab.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {!result ? (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained" onClick={submit} disabled={busy || !laneId}
              startIcon={busy ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
            >
              {busy ? 'Generating…' : 'Generate brief'}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={onClose}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function CellsTable({ cells }) {
  const [genCell, setGenCell] = useState(null);
  if (!cells.length) return <Typography variant="caption">Loading cells…</Typography>;
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Cell</TableCell>
            <TableCell>Setting</TableCell>
            <TableCell>Talent</TableCell>
            <TableCell>Opening 3s</TableCell>
            <TableCell>Format</TableCell>
            <TableCell>Audio</TableCell>
            <TableCell>Arc</TableCell>
            <TableCell>Cost</TableCell>
            <TableCell>Perf</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {cells.map((cell) => (
            <TableRow key={cell.id}>
              <TableCell>
                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  {cell.is_canonical && (
                    <Tooltip title="Canonical (anchored on a real banger)">
                      <CheckCircleIcon fontSize="inherit" color="success" />
                    </Tooltip>
                  )}
                  <Typography variant="caption" fontWeight={600}>{cell.name}</Typography>
                  {cell.maturity === 'experimental' && (
                    <Tooltip title="Experimental — a new angle with no track record yet">
                      <Chip size="small" label="🧪 Experimental" color="warning" variant="outlined"
                        sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.62rem' } }} />
                    </Tooltip>
                  )}
                  {cell.maturity === 'retired' && (
                    <Chip size="small" label="Retired" variant="outlined"
                      sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.62rem' } }} />
                  )}
                </Stack>
              </TableCell>
              <TableCell><Typography variant="caption">{cell.axis_setting?.slice(0, 60)}…</Typography></TableCell>
              <TableCell><Typography variant="caption">{cell.axis_talent}</Typography></TableCell>
              <TableCell><Typography variant="caption">{cell.axis_opening_3s}</Typography></TableCell>
              <TableCell><Typography variant="caption">{cell.axis_format_pacing}</Typography></TableCell>
              <TableCell><Typography variant="caption">{cell.axis_audio_bed}</Typography></TableCell>
              <TableCell><Typography variant="caption">{cell.axis_narrative_arc}</Typography></TableCell>
              <TableCell><Chip size="small" label={cell.estimated_cost_tier} variant="outlined" /></TableCell>
              <TableCell>
                <Typography variant="caption">
                  {cell.avg_performance_score != null ? cell.avg_performance_score.toFixed(2) : '—'}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Generate one brief from this exact cell">
                  <IconButton size="small" onClick={() => setGenCell(cell)}>
                    <AutoFixHighIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {genCell && (
        <GenerateFromCellDialog cell={genCell} onClose={() => setGenCell(null)} />
      )}
    </TableContainer>
  );
}

// ---------------------------------------------------------------------------
// BRIEFS TAB — list + generate-flight dialog + detail view
// ---------------------------------------------------------------------------

function BriefsTab() {
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [selectedBrief, setSelectedBrief] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [expanded, setExpanded] = useState({}); // {briefId: true}
  const [deployBrief, setDeployBrief] = useState(null); // brief pending the Deploy dialog

  const refresh = useCallback(() => {
    setLoading(true);
    listLaneBriefs(filterStatus ? { status: filterStatus } : {})
      .then((res) => setBriefs(asArray(res)))
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [filterStatus]);

  useEffect(() => { refresh(); }, [refresh]);

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const handleDeployDone = useCallback((b, d) => {
    setExpanded((p) => ({ ...p, [b.id]: true }));
    refresh();
    if (d?.skipped?.length) {
      setError(
        `Deployed ${d.created + d.updated} client(s). Skipped: ` +
        d.skipped.map((s) => `${s.client} (${s.reason})`).join(', '),
      );
    }
  }, [refresh]);

  // Group briefs by week (their concept_cell flight); ungrouped last.
  const groups = [];
  const byKey = {};
  for (const b of briefs) {
    const key = b.flight ? `f${b.flight}` : 'none';
    if (!byKey[key]) {
      byKey[key] = {
        key,
        label: b.flight
          ? (b.flight_name || b.flight_code || `Week #${b.flight}`)
          : 'Ungrouped (no week)',
        rows: [],
      };
      groups.push(byKey[key]);
    }
    byKey[key].rows.push(b);
  }

  const COLS = 8;
  const canDeploy = (b) => b.lane && [
    'approved', 'in_production', 'delivered', 'launched',
  ].includes(b.status);

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {briefs.length} brief(s). Status drives downstream actions (approved → designer, rejected → regenerate).
        </Typography>
        <TextField
          select
          label="Status filter"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          size="small"
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="judge_pending">Judge pending</MenuItem>
          <MenuItem value="judge_rejected">Rejected</MenuItem>
          <MenuItem value="in_production">In production</MenuItem>
          <MenuItem value="delivered">Delivered</MenuItem>
          <MenuItem value="launched">Launched</MenuItem>
          <MenuItem value="retired">Retired</MenuItem>
        </TextField>
        <IconButton onClick={refresh} disabled={loading} size="small">
          <RefreshIcon />
        </IconButton>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => setShowManual(true)}
        >
          Custom brief
        </Button>
        <Button
          variant="contained"
          startIcon={<AutoFixHighIcon />}
          onClick={() => setShowGenerate(true)}
        >
          Generate flight
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {loading && <LinearProgress />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Master / Cell</TableCell>
              <TableCell>Lane · Vertical</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Designer</TableCell>
              <TableCell align="right">Judge</TableCell>
              <TableCell align="right">Variants</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((g) => (
              <React.Fragment key={g.key}>
                <TableRow>
                  <TableCell colSpan={COLS} sx={{ bgcolor: 'grey.100' }}>
                    <Typography variant="caption" fontWeight={700}>{g.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {' '}· {g.rows.length} master(s)
                    </Typography>
                  </TableCell>
                </TableRow>
                {g.rows.map((b) => (
                  <React.Fragment key={b.id}>
                    <TableRow hover>
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          onClick={() => toggle(b.id)}
                          disabled={!b.distribution_count}
                        >
                          <ExpandMoreIcon
                            fontSize="small"
                            sx={{
                              transform: expanded[b.id] ? 'none' : 'rotate(-90deg)',
                              transition: 'transform 0.15s',
                            }}
                          />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontWeight={600}>
                          {b.vertical_label ? `${b.vertical_label} · ` : ''}{b.concept_name} · {b.brief_code}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {b.cell_name ? `Style: ${b.cell_name}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{b.lane_name || '— (no lane)'}</Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {b.vertical_label}
                        </Typography>
                      </TableCell>
                      <TableCell><BriefStatusChip status={b.status} /></TableCell>
                      <TableCell>
                        {b.task ? (
                          <Stack spacing={0.25}>
                            <TaskStatusChip status={b.task.status} statusDisplay={b.task.status_display} />
                            <Typography variant="caption" color="text.secondary">
                              {b.task.designer_name || b.task.designer_group_name || '—'}
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">unassigned</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">
                          {b.judge_score != null ? b.judge_score.toFixed(2) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">{b.distribution_count || 0}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {canDeploy(b) && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setDeployBrief(b)}
                            >
                              {b.distribution_count ? 'Re-deploy' : 'Deploy'}
                            </Button>
                          )}
                          <IconButton size="small" onClick={() => setSelectedBrief(b.id)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                    {expanded[b.id] && (b.distributions || []).map((d) => (
                      <TableRow key={`${b.id}-${d.id}`} sx={{ bgcolor: 'grey.50' }}>
                        <TableCell padding="checkbox" />
                        <TableCell sx={{ pl: 4 }}>
                          <Typography variant="caption">{d.client_name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {d.offer || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell colSpan={2}>
                          {d.task ? (
                            <Stack spacing={0.25}>
                              <TaskStatusChip status={d.task.status} statusDisplay={d.task.status_display} />
                              <Typography variant="caption" color="text.secondary">
                                {d.task.designer_name || 'unassigned'}
                                {d.task.revision_round > 0 ? ` · R${d.task.revision_round}` : ''}
                              </Typography>
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary">no task yet</Typography>
                          )}
                        </TableCell>
                        <TableCell colSpan={2} align="right">
                          {d.output_url ? (
                            <Link href={d.output_url} target="_blank" rel="noopener" variant="caption">
                              view variant
                            </Link>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {d.status_display || d.status}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell padding="checkbox" />
                      </TableRow>
                    ))}
                    {expanded[b.id] && !(b.distributions || []).length && (
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell padding="checkbox" />
                        <TableCell colSpan={COLS - 1}>
                          <Typography variant="caption" color="text.secondary">
                            No client variants yet — Deploy to fan this master to the lane's clients.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
            {!briefs.length && !loading && (
              <TableRow>
                <TableCell colSpan={COLS} align="center">
                  <Typography variant="caption" color="text.secondary">
                    No briefs yet. Click "Generate flight" to create some.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <GenerateFlightDialog
        open={showGenerate}
        onClose={() => setShowGenerate(false)}
        onCreated={() => { setShowGenerate(false); refresh(); }}
      />
      <ManualBriefDialog
        open={showManual}
        onClose={() => setShowManual(false)}
        onCreated={() => { setShowManual(false); refresh(); }}
      />
      {selectedBrief && (
        <BriefDetailDialog
          briefId={selectedBrief}
          onClose={() => setSelectedBrief(null)}
          onChanged={refresh}
        />
      )}
      {deployBrief && (
        <DeployDialog
          brief={deployBrief}
          onClose={() => setDeployBrief(null)}
          onDone={(d) => { handleDeployDone(deployBrief, d); setDeployBrief(null); }}
        />
      )}
    </Stack>
  );
}

// Mondays for the current week + the next N (value = ISO Monday date).
function buildWeekOptions(n = 6) {
  const opts = [];
  const now = new Date();
  const dow = now.getDay(); // 0=Sun .. 6=Sat
  const monday = new Date(now);
  monday.setDate(now.getDate() + (dow === 0 ? -6 : 1 - dow));
  monday.setHours(0, 0, 0, 0);
  const fmt = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  for (let i = 0; i < n; i += 1) {
    const m = new Date(monday); m.setDate(monday.getDate() + i * 7);
    const s = new Date(m); s.setDate(m.getDate() + 6);
    const iso = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}-${String(m.getDate()).padStart(2, '0')}`;
    const tag = i === 0 ? ' (this week)' : i === 1 ? ' (next week)' : '';
    opts.push({ value: iso, label: `Week of ${fmt(m)} – ${fmt(s)}${tag}` });
  }
  return opts;
}

function DeployDialog({ brief, onClose, onDone }) {
  const weekOpts = useMemo(() => buildWeekOptions(6), []);
  const [weekStart, setWeekStart] = useState(weekOpts[0]?.value || '');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Optional assign-at-deploy. "" = deploy only; "d:<id>" / "g:<id>".
  const [target, setTarget] = useState('');
  const [designers, setDesigners] = useState([]);
  const [groups, setGroups] = useState([]);
  useEffect(() => {
    listDesignerProfiles({ active: true })
      .then((r) => setDesigners(asArray(r))).catch(() => setDesigners([]));
    listDesignerGroups({ active: true })
      .then((r) => setGroups(asArray(r))).catch(() => setGroups([]));
  }, []);

  const loadPreview = useCallback(() => {
    setLoading(true);
    deployLaneBrief(brief.id, { commit: false, weekStart })
      .then((r) => setPreview(unwrap(r)))
      .catch((e) => setErr(e?.response?.data?.detail || String(e)))
      .finally(() => setLoading(false));
  }, [brief.id, weekStart]);
  useEffect(() => { loadPreview(); }, [loadPreview]);

  const locked = !!preview?.week_locked;

  const [tKind, tId] = target ? target.split(':') : ['', ''];

  const confirm = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await deployLaneBrief(brief.id, {
        commit: true,
        weekStart: locked ? '' : weekStart,
        designerId: tKind === 'd' ? tId : '',
        designerGroupId: tKind === 'g' ? tId : '',
      });
      onDone?.(unwrap(r));
    } catch (e) {
      setErr(e?.response?.data?.detail || String(e));
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {brief.distribution_count ? 'Re-deploy' : 'Deploy'} {brief.brief_code}
        {preview?.lane ? ` → ${preview.lane}` : ''}
      </DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        {loading ? (
          <LinearProgress />
        ) : (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {locked ? (
              <Alert severity="info">
                Already in <strong>{preview.current_flight || 'a flight'}</strong>.
                Re-deploy keeps it there — the week can't change after the first deploy
                (that would be a separate "move" operation).
              </Alert>
            ) : (
              <TextField
                select
                size="small"
                fullWidth
                label="Target week"
                value={weekStart}
                onChange={(e) => setWeekStart(e.target.value)}
                helperText="Which weekly flight these per-client tasks land in"
              >
                {weekOpts.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              select
              size="small"
              fullWidth
              label="Designer (optional)"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              helperText="Pick one to deploy AND assign in one step. Leave blank to assign later."
            >
              <MenuItem value=""><em>— deploy only (assign later) —</em></MenuItem>
              {designers.length > 0 && <ListSubheader>Designers</ListSubheader>}
              {designers.map((d) => (
                <MenuItem key={`d${d.id}`} value={`d:${d.id}`}>
                  {d.display_name || d.slug}
                </MenuItem>
              ))}
              {groups.length > 0 && <ListSubheader>Groups</ListSubheader>}
              {groups.map((g) => (
                <MenuItem key={`g${g.id}`} value={`g:${g.id}`}>
                  {g.name} (group)
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="body2">
              Fans out to <strong>{preview?.eligible_clients || 0}</strong> client(s)
              {preview?.target_week_label ? ` · ${preview.target_week_label}` : ''} —
              one per-client task each.
            </Typography>
            {preview?.clients?.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {preview.clients.map((c) => (
                  <Chip key={c.client_id} size="small" variant="outlined"
                    label={`${c.client_name}${c.offer ? ` · ${c.offer}` : ''}`} />
                ))}
              </Box>
            )}
            {preview?.skipped?.length > 0 && (
              <Alert severity="warning">
                Skipped: {preview.skipped.map((s) => `${s.client} (${s.reason})`).join(', ')}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          onClick={confirm}
          disabled={busy || loading || !preview?.eligible_clients}
        >
          {busy
            ? 'Deploying…'
            : `${target ? 'Deploy & assign' : 'Deploy'} to ${preview?.eligible_clients || 0} client(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BriefStatusChip({ status }) {
  const colorMap = {
    draft: 'default',
    judge_pending: 'warning',
    judge_rejected: 'error',
    approved: 'success',
    in_production: 'info',
    delivered: 'info',
    launched: 'primary',
    retired: 'default',
  };
  return <Chip size="small" label={status} color={colorMap[status] || 'default'} variant="outlined" />;
}

// Workflow state of the CreativeTask backing a brief (same state machine the
// legacy flights use): DRAFT → ASSIGNED → IN_PROGRESS → SUBMITTED →
// NEEDS_REVISION → APPROVED.
function TaskStatusChip({ status, statusDisplay }) {
  const colorMap = {
    draft: 'default',
    assigned: 'info',
    in_progress: 'warning',
    submitted: 'secondary',
    needs_revision: 'error',
    approved: 'success',
  };
  return (
    <Chip
      size="small"
      label={statusDisplay || status}
      color={colorMap[status] || 'default'}
    />
  );
}

// Per-brief assign-to-designer + workflow status. Assigning bridges the brief
// into the concept_cell CreativeFlight/CreativeTask pipeline (kept isolated
// from legacy flights); submit / request-revision / approve then run through
// the existing CreativeTask endpoints — the brief markdown is the spec.
function AssignPanel({ brief, busy, onAssigned }) {
  const [designers, setDesigners] = useState([]);
  const [groups, setGroups] = useState([]);
  const [target, setTarget] = useState(''); // "d:<id>" | "g:<id>"
  const [working, setWorking] = useState(false);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(false);

  const task = brief?.task || null;

  useEffect(() => {
    listDesignerProfiles({ active: true })
      .then((res) => setDesigners(asArray(res)))
      .catch(() => setDesigners([]));
    listDesignerGroups({ active: true })
      .then((res) => setGroups(asArray(res)))
      .catch(() => setGroups([]));
  }, []);

  useEffect(() => {
    if (task?.designer) setTarget(`d:${task.designer}`);
    else if (task?.designer_group) setTarget(`g:${task.designer_group}`);
  }, [task?.designer, task?.designer_group]);

  const doAssign = async () => {
    if (!target) return;
    setWorking(true); setErr(null);
    const [kind, idStr] = target.split(':');
    const id = Number(idStr);
    try {
      await assignLaneBrief(brief.id, kind === 'd'
        ? { designerId: id }
        : { designerGroupId: id });
      setEditing(false);
      await onAssigned?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || String(e));
    } finally {
      setWorking(false);
    }
  };

  const doUnassign = async () => {
    if (!window.confirm('Pull this brief back from the designer?')) return;
    setWorking(true); setErr(null);
    try {
      await unassignLaneBrief(brief.id);
      await onAssigned?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || String(e));
    } finally {
      setWorking(false);
    }
  };

  const canUnassign = task && ['draft', 'assigned'].includes(task.status);
  const selector = (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        select
        size="small"
        label="Designer or group"
        value={target}
        onChange={(e) => setTarget(e.target.value)}
        sx={{ minWidth: 260 }}
        disabled={working || busy}
      >
        <MenuItem value=""><em>— pick —</em></MenuItem>
        {designers.length > 0 && (
          <ListSubheader>Designers</ListSubheader>
        )}
        {designers.map((d) => (
          <MenuItem key={`d${d.id}`} value={`d:${d.id}`}>
            {d.display_name || d.slug}
          </MenuItem>
        ))}
        {groups.length > 0 && (
          <ListSubheader>Groups</ListSubheader>
        )}
        {groups.map((g) => (
          <MenuItem key={`g${g.id}`} value={`g:${g.id}`}>
            {g.name} (group)
          </MenuItem>
        ))}
      </TextField>
      <Button
        variant="contained"
        size="small"
        onClick={doAssign}
        disabled={!target || working || busy}
      >
        {task ? 'Reassign' : 'Assign to designer'}
      </Button>
      {task && (
        <Button size="small" onClick={() => setEditing(false)} disabled={working}>
          Cancel
        </Button>
      )}
    </Stack>
  );

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack spacing={2}>
        {err && <Alert severity="error">{err}</Alert>}

        {!task && (
          <>
            <Typography variant="body2" color="text.secondary">
              Hand this brief to a designer. It enters the Creative Engine
              flight workflow (separate from legacy weekly flights) and shows
              up in the designer's queue with the brief markdown as the spec.
            </Typography>
            {selector}
          </>
        )}

        {task && (
          <>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle2">Workflow</Typography>
              <TaskStatusChip status={task.status} statusDisplay={task.status_display} />
              {task.revision_round > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  color="error"
                  label={`revision ${task.revision_round}`}
                />
              )}
            </Stack>

            <Stack spacing={0.5}>
              <KV k="Assigned to" v={task.designer_name || task.designer_group_name || '—'} />
              <KV
                k="Covers"
                v={`${brief.lane_name ? `${brief.lane_name} · ` : ''}${brief.distribution_count || 0} client(s) — one master → a separate per-client task each (deploy creates them)`}
              />
              {(brief.distributions || []).length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: '108px', mt: 0.25 }}>
                  {brief.distributions.map((d) => (
                    <Chip
                      key={d.id}
                      size="small"
                      variant="outlined"
                      label={`${d.client_name}${d.offer ? ` · ${d.offer}` : ''} — ${d.task ? (d.task.status_display || d.task.status) : 'no task'}`}
                    />
                  ))}
                </Box>
              )}
              <KV k="Flight" v={`${task.flight_code || '—'} · ${task.flight_name || ''}`} />
              <KV k="Task" v={task.task_code || `#${task.id}`} />
              <KV
                k="Frame.io"
                v={task.frame_io_url
                  ? <Link href={task.frame_io_url} target="_blank" rel="noopener">{task.frame_io_url}</Link>
                  : '— (designer adds on submit)'}
              />
              {task.submitted_at && (
                <KV k="Submitted" v={new Date(task.submitted_at).toLocaleString()} />
              )}
              {task.approved_at && (
                <KV k="Approved" v={new Date(task.approved_at).toLocaleString()} />
              )}
              {task.rating != null && <KV k="Rating" v={`${task.rating}/5`} />}
            </Stack>

            <Typography variant="caption" color="text.secondary">
              Submit, request-revision and approve run on the task via the
              Creative workflow (designer portal / tasks page) — this panel
              tracks status. Reassign while in DRAFT/ASSIGNED.
            </Typography>

            {editing ? selector : (
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setEditing(true)}
                  disabled={working || busy}
                >
                  Reassign
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={doUnassign}
                  disabled={!canUnassign || working || busy}
                  title={canUnassign ? '' : 'Designer has started — request a revision instead'}
                >
                  Unassign
                </Button>
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
}

function KV({ k, v }) {
  return (
    <Stack direction="row" spacing={1}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
        {k}
      </Typography>
      <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{v}</Typography>
    </Stack>
  );
}

// Shot Builder — per-shot human control. Default is AI; a designer can pin a
// shot to their own uploaded clip or mark it generic. Resolved at render time.
function ShotSourceChip({ mode }) {
  const map = { ai: ['AI', 'default'], upload: ['Uploaded', 'success'], generic: ['Generic', 'warning'] };
  const [label, color] = map[mode] || map.ai;
  return <Chip size="small" label={label} color={color} variant={mode === 'ai' ? 'outlined' : 'filled'} />;
}

function ShotsPanel({ brief, onChanged }) {
  const [shots, setShots] = useState([]);
  const [hasShots, setHasShots] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyShot, setBusyShot] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    listBriefShots(brief.id)
      .then((res) => {
        const d = unwrap(res);
        setShots(d?.shots || []);
        setHasShots(!!d?.has_shots);
      })
      .catch((e) => setErr(String(e)))
      .finally(() => setLoading(false));
  }, [brief.id]);

  useEffect(() => { load(); }, [load]);

  const apply = async (shotId, mode, file) => {
    setBusyShot(shotId); setErr(null);
    try {
      const res = await setBriefShotSource(brief.id, { shotId, mode, file });
      const d = unwrap(res);
      setShots(d?.shots || []);
      setHasShots(!!d?.has_shots);
      onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || String(e));
    } finally {
      setBusyShot(null);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Every shot is AI-generated by default. Pin a shot to your own clip
          (product shot, real b-roll) or mark it generic — the system still
          lays the voiceover &amp; captions over it. Applied on the next render.
        </Typography>
        {err && <Alert severity="error">{err}</Alert>}
        {loading && <LinearProgress />}
        {!loading && !hasShots && (
          <Alert severity="info">
            No shot-list yet. The first AI render auto-expands this brief into
            shots (creative locked) — once it has, come back here to pin clips.
          </Alert>
        )}
        {hasShots && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Shot</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>What it is</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell align="right">Set</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shots.map((s) => (
                  <TableRow key={s.shot_id} hover>
                    <TableCell>
                      <Typography variant="caption" fontWeight={600}>{s.shot_id}</Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {s.visual_source}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {s.time_start ?? '?'}–{s.time_end ?? '?'}s
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="caption" display="block" sx={{ whiteSpace: 'normal' }}>
                        {s.vo_line || <i>(no VO)</i>}
                      </Typography>
                      {(s.visual_prompt || s.motion_prompt) && (
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                          {(s.visual_prompt || s.motion_prompt).slice(0, 120)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <ShotSourceChip mode={s.mode} />
                        {s.file_url && (
                          <Link href={s.file_url} target="_blank" rel="noopener" variant="caption">
                            {s.original_name || 'view clip'}
                          </Link>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      {busyShot === s.shot_id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant={s.mode === 'ai' ? 'contained' : 'outlined'}
                            onClick={() => apply(s.shot_id, 'ai')}
                          >
                            AI
                          </Button>
                          <Button
                            size="small"
                            variant={s.mode === 'upload' ? 'contained' : 'outlined'}
                            component="label"
                          >
                            Upload
                            <input
                              type="file"
                              accept="video/*"
                              hidden
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) apply(s.shot_id, 'upload', f);
                                e.target.value = '';
                              }}
                            />
                          </Button>
                          <Button
                            size="small"
                            variant={s.mode === 'generic' ? 'contained' : 'outlined'}
                            onClick={() => apply(s.shot_id, 'generic')}
                          >
                            Generic
                          </Button>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </Paper>
  );
}

function GenerateFlightDialog({ open, onClose, onCreated }) {
  // 'lane' = generate a master that fans out to a lane of clients (default).
  // 'client' = generate for a SINGLE client (legacy path; brief stays one-off).
  const [mode, setMode] = useState('lane');
  const [laneId, setLaneId] = useState('');
  const [lanes, setLanes] = useState([]);
  const [lanesLoading, setLanesLoading] = useState(false);
  const [verticals, setVerticals] = useState([]);
  const [verticalFilter, setVerticalFilter] = useState(''); // vertical_id, scoping only
  // Single-client mode state:
  const [clientId, setClientId] = useState('');
  const [clientOffers, setClientOffers] = useState([]);
  const [clientOffersLoading, setClientOffersLoading] = useState(false);
  const [clientOfferId, setClientOfferId] = useState('');
  const [targetCount, setTargetCount] = useState(3);
  const [costTierMax, setCostTierMax] = useState('');
  const [conceptSlugs, setConceptSlugs] = useState([]);
  const [extraSteering, setExtraSteering] = useState('');
  const [includeExperimental, setIncludeExperimental] = useState(false);
  const [allConcepts, setAllConcepts] = useState([]);
  // Client IDs in the lane that the operator has chosen to EXCLUDE from this
  // brief's fan-out. Default empty = fan to the whole lane.
  const [excludedClientIds, setExcludedClientIds] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const {
    options: clientOptions,
    loading: clientOptionsLoading,
    handleInputChange: handleClientSearch,
    findOptionByValue: findClientOptionByValue,
  } = useClientAutocomplete();

  useEffect(() => {
    if (!open) return;
    listAdConcepts().then((r) => setAllConcepts(asArray(r))).catch(() => {});
    fetchVerticals().then((r) => setVerticals(asArray(r))).catch(() => setVerticals([]));
    setLanesLoading(true);
    fetchLanes()
      .then((r) => setLanes(asArray(r)))
      .catch(() => setLanes([]))
      .finally(() => setLanesLoading(false));
  }, [open]);

  // Fetch the client's AD offers when one is picked in single-client mode.
  // Backend resolves the offer automatically if none is selected; the picker
  // is purely a "lock in a specific offer" override.
  useEffect(() => {
    if (mode !== 'client' || !clientId) {
      setClientOffers([]);
      setClientOfferId('');
      return;
    }
    setClientOffersLoading(true);
    fetchClientOffers({ client_id: clientId, offer_type: 'offer', page_size: 100 })
      .then((r) => setClientOffers(asArray(r)))
      .catch(() => setClientOffers([]))
      .finally(() => setClientOffersLoading(false));
  }, [mode, clientId]);

  // A different lane = a different client set, so a stale exclusion list is
  // meaningless. Wipe on lane change.
  useEffect(() => {
    setExcludedClientIds(new Set());
  }, [laneId]);

  // vertical_id → label, for lane labels + the scoping filter.
  const vName = (vid) => {
    const v = verticals.find((x) => String(x.id) === String(vid));
    return v ? (v.vertical || v.name || `Vertical ${vid}`) : (vid ? `Vertical ${vid}` : '');
  };
  const laneClientCount = (l) =>
    new Set((l?.assignments || []).map((a) => a.client || a.client_id)).size;

  // Lanes are 1:1 with a vertical, so the vertical picker only *scopes* the
  // lane list — it is never sent (the backend derives vertical from the lane).
  const visibleLanes = verticalFilter
    ? lanes.filter((l) => String(l.vertical_id) === String(verticalFilter))
    : lanes;
  const verticalsWithLanes = verticals.filter((v) =>
    lanes.some((l) => String(l.vertical_id) === String(v.id)));

  const selectedLane = lanes.find((l) => String(l.id) === String(laneId)) || null;

  const reset = useCallback(() => {
    setMode('lane');
    setLaneId(''); setVerticalFilter('');
    setClientId(''); setClientOffers([]); setClientOfferId('');
    setTargetCount(3); setCostTierMax(''); setConceptSlugs([]);
    setExtraSteering(''); setIncludeExperimental(false);
    setExcludedClientIds(new Set());
    setError(null); setResult(null);
  }, []);

  const submit = useCallback(async () => {
    setSubmitting(true); setError(null);
    try {
      const payload = { target_count: Number(targetCount) };
      if (mode === 'lane') {
        payload.lane_id = Number(laneId);
        if (excludedClientIds.size > 0) {
          payload.excluded_client_ids = Array.from(excludedClientIds).map(Number);
        }
      } else {
        payload.client_id = Number(clientId);
        if (clientOfferId) payload.client_offer_id = Number(clientOfferId);
      }
      if (costTierMax) payload.cost_tier_max = costTierMax;
      if (conceptSlugs.length > 0) payload.concept_slugs = conceptSlugs;
      if (extraSteering) payload.extra_steering = extraSteering;
      if (includeExperimental) payload.include_experimental = true;
      const r = await generateFlight(payload);
      setResult(unwrap(r));
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally {
      setSubmitting(false);
    }
  }, [mode, laneId, clientId, clientOfferId, targetCount, costTierMax,
      conceptSlugs, extraSteering, includeExperimental, excludedClientIds]);

  const close = () => { reset(); onClose(); };
  const handleDone = () => { reset(); onCreated(); };

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>Generate flight</DialogTitle>
      <DialogContent>
        {!result ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Composes N cells with Andromeda spread + class mix, calls Claude per cell, runs the
              Judge. <strong>Costs real API money</strong> (~$0.30 per brief).
            </Alert>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={mode}
              onChange={(_e, v) => { if (v) setMode(v); }}
              fullWidth
            >
              <ToggleButton value="lane">
                Lane (fans out to a client group)
              </ToggleButton>
              <ToggleButton value="client">
                Single client (one-off brief)
              </ToggleButton>
            </ToggleButtonGroup>
            <Grid container spacing={2}>
              {mode === 'lane' ? (
                <>
                  <Grid item xs={4}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Vertical"
                      required
                      value={verticalFilter}
                      onChange={(e) => { setVerticalFilter(e.target.value); setLaneId(''); }}
                      helperText="Pick a vertical, then its lane"
                    >
                      <MenuItem value=""><em>— select —</em></MenuItem>
                      {verticalsWithLanes.map((v) => (
                        <MenuItem key={v.id} value={String(v.id)}>
                          {v.vertical || v.name || `Vertical ${v.id}`}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={8}>
                    <Autocomplete
                      size="small"
                      options={visibleLanes}
                      loading={lanesLoading}
                      disabled={!verticalFilter}
                      getOptionLabel={(l) => {
                        if (!l) return '';
                        const v = vName(l.vertical_id);
                        return `${l.name}${v ? ` · ${v}` : ''}`;
                      }}
                      isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
                      value={selectedLane}
                      onChange={(_e, v) => setLaneId(v?.id ? String(v.id) : '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Lane"
                          placeholder={verticalFilter
                            ? 'Pick a lane (conflict-free client group)…'
                            : 'Pick a vertical first'}
                          required
                          helperText={verticalFilter
                            ? "One client-neutral master per cell. Vertical + a representative offer are derived from the lane; deploy fans it to the lane's clients."
                            : 'Disabled until a vertical is selected'}
                        />
                      )}
                    />
                  </Grid>
                  {selectedLane && (() => {
                    // Dedupe lane assignments by client_id and capture names.
                    const seen = new Map();
                    (selectedLane.assignments || []).forEach((a) => {
                      const cid = a.client_id ?? a.client;
                      if (cid && !seen.has(cid)) {
                        seen.set(cid, a.client_name || `Client ${cid}`);
                      }
                    });
                    const laneClients = Array.from(seen, ([id, name]) => ({ id, name }))
                      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
                    const total = laneClients.length;
                    const includedCount = laneClients.filter((c) => !excludedClientIds.has(c.id)).length;
                    const toggleClient = (id) => {
                      setExcludedClientIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(id)) next.delete(id); else next.add(id);
                        return next;
                      });
                    };
                    const selectAll = () => setExcludedClientIds(new Set());
                    const selectNone = () => setExcludedClientIds(new Set(laneClients.map((c) => c.id)));
                    return (
                      <Grid item xs={12}>
                        <Alert severity="success" variant="outlined" sx={{ mb: 1 }}>
                          Lane <strong>{selectedLane.name}</strong>
                          {vName(selectedLane.vertical_id)
                            ? <> · vertical <strong>{vName(selectedLane.vertical_id)}</strong></>
                            : null}
                          {' '}· deploying to <strong>{includedCount}</strong> of {total} client(s)
                          {' '}— the master is generated client-neutral; deploy fans it to the included clients after the Judge approves.
                        </Alert>
                        <Accordion variant="outlined" defaultExpanded sx={{ '&:before': { display: 'none' } }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight={600}>
                                Clients in this lane ({includedCount}/{total} selected)
                              </Typography>
                              <Box sx={{ flex: 1 }} />
                              <Button size="small" onClick={(e) => { e.stopPropagation(); selectAll(); }}
                                disabled={excludedClientIds.size === 0}>
                                All
                              </Button>
                              <Button size="small" onClick={(e) => { e.stopPropagation(); selectNone(); }}
                                disabled={includedCount === 0}>
                                None
                              </Button>
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0 }}>
                            <Grid container spacing={0.5}>
                              {laneClients.map((c) => (
                                <Grid item xs={12} sm={6} md={4} key={c.id}>
                                  <FormControlLabel
                                    sx={{ width: '100%' }}
                                    control={(
                                      <Switch
                                        size="small"
                                        checked={!excludedClientIds.has(c.id)}
                                        onChange={() => toggleClient(c.id)}
                                      />
                                    )}
                                    label={<Typography variant="body2">{c.name}</Typography>}
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      </Grid>
                    );
                  })()}
                </>
              ) : (
                <>
                  <Grid item xs={6}>
                    <Autocomplete
                      size="small"
                      options={clientOptions}
                      loading={clientOptionsLoading}
                      getOptionLabel={(o) => o?.label || ''}
                      isOptionEqualToValue={(o, v) => String(o.value) === String(v?.value)}
                      value={findClientOptionByValue(clientId)}
                      onInputChange={handleClientSearch}
                      onChange={(_e, v) => setClientId(v?.value ? String(v.value) : '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Client"
                          required
                          placeholder="Search for a client…"
                          helperText="Brief is generated for this client only — not fanned out."
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Offer (optional)"
                      value={clientOfferId}
                      onChange={(e) => setClientOfferId(e.target.value)}
                      disabled={!clientId || clientOffersLoading}
                      helperText={clientId
                        ? (clientOffersLoading
                            ? 'Loading offers…'
                            : 'Leave blank to auto-resolve the client\'s current AD offer.')
                        : 'Pick a client first'}
                    >
                      <MenuItem value=""><em>Auto-resolve current offer</em></MenuItem>
                      {clientOffers.map((o) => (
                        <MenuItem key={o.id} value={String(o.id)}>
                          {o.display_label || o.custom_label || `Offer ${o.id}`}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </>
              )}
              <Grid item xs={6}>
                <TextField
                  label="How many briefs to generate?"
                  type="number"
                  value={targetCount}
                  onChange={(e) => setTargetCount(e.target.value)}
                  inputProps={{ min: 1, max: 20 }}
                  size="small"
                  fullWidth
                  helperText="One Claude call per brief (~$0.30 each)"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  label="Production cost cap"
                  value={costTierMax}
                  onChange={(e) => setCostTierMax(e.target.value)}
                  size="small"
                  fullWidth
                  helperText="Skip cells more expensive than this"
                >
                  <MenuItem value="">Any (no cap)</MenuItem>
                  <MenuItem value="low">Low only (graphics / slideshow / animation)</MenuItem>
                  <MenuItem value="medium">Up to medium (+ basic talent shoots)</MenuItem>
                  <MenuItem value="high">All tiers (incl. live multi-talent shoots)</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <Autocomplete
                  multiple
                  size="small"
                  options={allConcepts}
                  getOptionLabel={(o) => o.name || o.slug || ''}
                  isOptionEqualToValue={(o, v) => o.slug === v.slug}
                  value={allConcepts.filter((c) => conceptSlugs.includes(c.slug))}
                  onChange={(_e, v) => setConceptSlugs(v.map((c) => c.slug))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Restrict to concepts (optional)"
                      placeholder="All concepts if blank"
                      helperText="Pick specific concepts, or leave blank for all"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="One-off steering (optional)"
                  value={extraSteering}
                  onChange={(e) => setExtraSteering(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                  helperText="Extra direction prepended to this flight's generation (e.g. 'lean into trust signals')"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Switch
                      size="small"
                      checked={includeExperimental}
                      onChange={(e) => setIncludeExperimental(e.target.checked)}
                    />
                  )}
                  label={(
                    <Typography variant="caption" color="text.secondary">
                      Include 🧪 experimental cells — untested angles (e.g. ideas
                      decomposed from ads found in the wild). Off = proven recipes only.
                    </Typography>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" variant="outlined">
                  Briefs are generated as human-readable designer specs. The
                  machine shot-list for AI render is added automatically the
                  first time you hit “AI render” on a brief — no toggle, no
                  double-pay on briefs you never render.
                </Alert>
              </Grid>
            </Grid>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="success">{result.summary}</Alert>
            <Typography variant="caption" color="text.secondary">
              Composition: {result.composition_notes}
            </Typography>
            <Typography variant="caption">
              Min pairwise distance: {result.min_pairwise_distance} · Authenticity class: {String(result.has_authenticity_class)}
            </Typography>
            <Divider />
            {(result.briefs || []).map((b) => (
              <Card key={b.id} variant="outlined">
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="caption" fontWeight={600}>{b.brief_code}</Typography>
                    <Chip size="small" label={b.concept_name} />
                    <Typography variant="caption" color="text.secondary">{b.cell_name}</Typography>
                    <Box sx={{ flex: 1 }} />
                    <BriefStatusChip status={b.status} />
                    <Typography variant="caption">
                      judge: {b.judge_score != null ? b.judge_score.toFixed(2) : '—'}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ))}
            {(result.failed_cells || []).length > 0 && (
              <Alert severity="warning">
                Failed cells: {result.failed_cells.map((f) => f.cell_name).join(', ')}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {!result ? (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={submitting ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
              onClick={submit}
              disabled={submitting || (mode === 'lane' ? !laneId : !clientId)}
            >
              {submitting ? 'Generating…' : 'Run'}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={handleDone}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ManualBriefDialog({ open, onClose, onCreated }) {
  // Designer-authored brief: pick a cell (so it slots into the matrix), pick
  // the destination (lane or single client), paste markdown. No AI, no Judge.
  const [mode, setMode] = useState('lane');
  const [laneId, setLaneId] = useState('');
  const [lanes, setLanes] = useState([]);
  const [lanesLoading, setLanesLoading] = useState(false);
  const [verticals, setVerticals] = useState([]);
  const [verticalFilter, setVerticalFilter] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientOffers, setClientOffers] = useState([]);
  const [clientOffersLoading, setClientOffersLoading] = useState(false);
  const [clientOfferId, setClientOfferId] = useState('');
  const [concepts, setConcepts] = useState([]);
  const [conceptSlug, setConceptSlug] = useState('');
  const [conceptDetail, setConceptDetail] = useState(null);
  const [conceptLoading, setConceptLoading] = useState(false);
  const [cellId, setCellId] = useState('');
  const [markdown, setMarkdown] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(false);

  const {
    options: clientOptions,
    loading: clientOptionsLoading,
    handleInputChange: handleClientSearch,
    findOptionByValue: findClientOptionByValue,
  } = useClientAutocomplete();

  useEffect(() => {
    if (!open) return;
    listAdConcepts().then((r) => setConcepts(asArray(r))).catch(() => {});
    fetchVerticals().then((r) => setVerticals(asArray(r))).catch(() => setVerticals([]));
    setLanesLoading(true);
    fetchLanes()
      .then((r) => setLanes(asArray(r)))
      .catch(() => setLanes([]))
      .finally(() => setLanesLoading(false));
  }, [open]);

  // Load the concept's cells when picked, so the cell dropdown is populated.
  useEffect(() => {
    if (!conceptSlug) {
      setConceptDetail(null);
      setCellId('');
      return;
    }
    setConceptLoading(true);
    setCellId('');
    fetchAdConcept(conceptSlug)
      .then((r) => setConceptDetail(unwrap(r)))
      .catch(() => setConceptDetail(null))
      .finally(() => setConceptLoading(false));
  }, [conceptSlug]);

  useEffect(() => {
    if (mode !== 'client' || !clientId) {
      setClientOffers([]);
      setClientOfferId('');
      return;
    }
    setClientOffersLoading(true);
    fetchClientOffers({ client_id: clientId, offer_type: 'offer', page_size: 100 })
      .then((r) => setClientOffers(asArray(r)))
      .catch(() => setClientOffers([]))
      .finally(() => setClientOffersLoading(false));
  }, [mode, clientId]);

  const vName = (vid) => {
    const v = verticals.find((x) => String(x.id) === String(vid));
    return v ? (v.vertical || v.name || `Vertical ${vid}`) : (vid ? `Vertical ${vid}` : '');
  };
  const visibleLanes = verticalFilter
    ? lanes.filter((l) => String(l.vertical_id) === String(verticalFilter))
    : lanes;
  const verticalsWithLanes = verticals.filter((v) =>
    lanes.some((l) => String(l.vertical_id) === String(v.id)));
  const selectedLane = lanes.find((l) => String(l.id) === String(laneId)) || null;
  const cellOptions = (conceptDetail?.sub_axis_cells || conceptDetail?.cells || []);

  const reset = useCallback(() => {
    setMode('lane');
    setLaneId(''); setVerticalFilter('');
    setClientId(''); setClientOffers([]); setClientOfferId('');
    setConceptSlug(''); setConceptDetail(null); setCellId('');
    setMarkdown(''); setNotes('');
    setError(null); setPreview(false);
  }, []);

  const submit = useCallback(async () => {
    setSubmitting(true); setError(null);
    try {
      const payload = { rendered_brief: markdown };
      // Cell is optional — backend falls back to the "Freeform" cell, which
      // lives outside the Andromeda matrix.
      if (cellId) payload.cell_id = Number(cellId);
      if (mode === 'lane') {
        payload.lane_id = Number(laneId);
      } else {
        payload.client_id = Number(clientId);
        if (clientOfferId) payload.client_offer_id = Number(clientOfferId);
      }
      if (notes.trim()) payload.notes = notes.trim();
      await createManualBrief(payload);
      reset();
      onCreated();
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally {
      setSubmitting(false);
    }
  }, [mode, laneId, clientId, clientOfferId, cellId, markdown, notes, reset, onCreated]);

  const close = () => { reset(); onClose(); };

  // Concept + cell are optional. If concept is picked, cell becomes required
  // (we need a specific recipe within it). If concept is blank, brief lands
  // freeform (no matrix slotting).
  const destReady = mode === 'lane' ? !!laneId : !!clientId;
  const cellOk = !conceptSlug || !!cellId;
  const canSubmit = destReady && cellOk && markdown.trim().length > 20;

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>Custom brief (designer-authored)</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            For when you have a sharp idea and want to hand-write the script
            instead of generating it. No AI cost, no Judge step — the brief
            lands as <strong>Approved</strong> and is ready to deploy or assign.
            Still slots into the matrix via the cell you pick.
          </Alert>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(_e, v) => { if (v) setMode(v); }}
            fullWidth
          >
            <ToggleButton value="lane">Lane (fan out)</ToggleButton>
            <ToggleButton value="client">Single client</ToggleButton>
          </ToggleButtonGroup>

          <Grid container spacing={2}>
            {mode === 'lane' ? (
              <>
                <Grid item xs={4}>
                  <TextField
                    select size="small" fullWidth required label="Vertical"
                    value={verticalFilter}
                    onChange={(e) => { setVerticalFilter(e.target.value); setLaneId(''); }}
                  >
                    <MenuItem value=""><em>— select —</em></MenuItem>
                    {verticalsWithLanes.map((v) => (
                      <MenuItem key={v.id} value={String(v.id)}>
                        {v.vertical || v.name || `Vertical ${v.id}`}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={8}>
                  <Autocomplete
                    size="small"
                    options={visibleLanes}
                    loading={lanesLoading}
                    disabled={!verticalFilter}
                    getOptionLabel={(l) => l ? `${l.name} · ${vName(l.vertical_id)}` : ''}
                    isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
                    value={selectedLane}
                    onChange={(_e, v) => setLaneId(v?.id ? String(v.id) : '')}
                    renderInput={(params) => (
                      <TextField {...params} label="Lane" required
                        helperText={verticalFilter ? 'Brief fans out to all clients in the lane.' : 'Pick a vertical first'} />
                    )}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={6}>
                  <Autocomplete
                    size="small"
                    options={clientOptions}
                    loading={clientOptionsLoading}
                    getOptionLabel={(o) => o?.label || ''}
                    isOptionEqualToValue={(o, v) => String(o.value) === String(v?.value)}
                    value={findClientOptionByValue(clientId)}
                    onInputChange={handleClientSearch}
                    onChange={(_e, v) => setClientId(v?.value ? String(v.value) : '')}
                    renderInput={(params) => (
                      <TextField {...params} label="Client" required
                        placeholder="Search for a client…" />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select size="small" fullWidth
                    label="Offer (optional)"
                    value={clientOfferId}
                    onChange={(e) => setClientOfferId(e.target.value)}
                    disabled={!clientId || clientOffersLoading}
                    helperText={clientId
                      ? (clientOffersLoading
                          ? 'Loading offers…'
                          : 'Blank = auto-resolve current AD offer.')
                      : 'Pick a client first'}
                  >
                    <MenuItem value=""><em>Auto-resolve current offer</em></MenuItem>
                    {clientOffers.map((o) => (
                      <MenuItem key={o.id} value={String(o.id)}>
                        {o.display_label || o.custom_label || `Offer ${o.id}`}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}

            <Grid item xs={6}>
              <TextField
                select size="small" fullWidth label="Concept (optional)"
                value={conceptSlug}
                onChange={(e) => setConceptSlug(e.target.value)}
                helperText={conceptSlug
                  ? 'Slots into the matrix under this concept.'
                  : 'Freeform — no matrix slotting.'}
              >
                <MenuItem value=""><em>Freeform (no concept)</em></MenuItem>
                {concepts.map((c) => (
                  <MenuItem key={c.slug} value={c.slug}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              {conceptSlug ? (
                <TextField
                  select size="small" fullWidth required label="Cell (recipe)"
                  value={cellId}
                  onChange={(e) => setCellId(e.target.value)}
                  disabled={conceptLoading}
                  helperText={conceptLoading
                    ? 'Loading cells…'
                    : 'Which recipe is this script?'}
                >
                  <MenuItem value=""><em>— select —</em></MenuItem>
                  {cellOptions.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.name}{c.maturity === 'experimental' ? ' 🧪' : ''}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                  Lands in the freeform bucket — won't be matched by the AI composer.
                </Alert>
              )}
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  Your script (markdown). Use <code>{'{{client.ad_offer}}'}</code> and
                  {' '}<code>{'{{client.logo}}'}</code> for dynamic per-client values.
                </Typography>
                <FormControlLabel
                  control={<Switch size="small" checked={preview} onChange={(e) => setPreview(e.target.checked)} />}
                  label={<Typography variant="caption">Preview</Typography>}
                />
              </Stack>
              {preview ? (
                <Paper variant="outlined" sx={{ p: 2, minHeight: 280, bgcolor: 'background.default' }}>
                  <Box sx={MD_SX}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {markdown || '_(nothing yet)_'}
                    </ReactMarkdown>
                  </Box>
                </Paper>
              ) : (
                <BriefMarkdownEditor
                  value={markdown}
                  onChange={setMarkdown}
                  placeholder={'# Your headline\n\n## Hook (0-3s)\n…'}
                />
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                size="small" fullWidth
                label="Internal notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline minRows={2}
                helperText="Notes for yourself / the team — not shown in the designer brief."
              />
            </Grid>
          </Grid>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={14} /> : <SaveIcon />}
          onClick={submit}
          disabled={submitting || !canSubmit}
        >
          {submitting ? 'Saving…' : 'Create brief'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BriefDetailDialog({ briefId, onClose, onChanged }) {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('rendered');
  const [busy, setBusy] = useState(false);
  const [renderedAssets, setRenderedAssets] = useState([]);
  const [rendering, setRendering] = useState(false);
  const [renderTier, setRenderTier] = useState('fast');  // 'fast' (~$2/render) | 'pro' (~$7.50/render)
  const [lipSync, setLipSync] = useState(false);  // adds Sync.so (~$6/render, glitchy on AI faces)
  const [regenSel, setRegenSel] = useState({});  // { assetId: Set(shot_id) } shots to regenerate on recompose
  const [recomposingId, setRecomposingId] = useState(null);
  const [captionsMode, setCaptionsMode] = useState('auto');  // auto | highlight | word | keyword | off
  // Script-edit state — the master brief is the source of truth and every
  // per-client task reads it live, so editing here cascades automatically.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [savingScript, setSavingScript] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetchLaneBrief(briefId)
      .then((res) => setBrief(unwrap(res)))
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
    listRenderedAssets(briefId)
      .then((res) => setRenderedAssets(asArray(res)))
      .catch(() => setRenderedAssets([]));
  }, [briefId]);

  useEffect(() => { load(); }, [load]);

  // Renders run on Celery now (5-15 min) — the API returns immediately with a
  // pending asset. Poll while anything is still pending/rendering so the UI
  // advances to completed/failed on its own.
  useEffect(() => {
    const inFlight = renderedAssets.some(
      (a) => a.status === 'pending' || a.status === 'rendering',
    );
    if (!inFlight) return undefined;
    const id = setInterval(() => {
      listRenderedAssets(briefId)
        .then((res) => setRenderedAssets(asArray(res)))
        .catch(() => {});
    }, 15000);
    return () => clearInterval(id);
  }, [renderedAssets, briefId]);

  const toggleRegen = (assetId, shotId) => {
    setRegenSel((prev) => {
      const cur = new Set(prev[assetId] || []);
      if (cur.has(shotId)) cur.delete(shotId); else cur.add(shotId);
      return { ...prev, [assetId]: cur };
    });
  };

  const handleRecompose = async (asset) => {
    const ids = Array.from(regenSel[asset.id] || []);
    setRecomposingId(asset.id); setError(null);
    try {
      await recomposeLaneBrief(briefId, {
        sourceAssetId: asset.id,
        regenerateShotIds: ids,
        tier: renderTier,
        captionsMode,
      });
      setRegenSel((prev) => ({ ...prev, [asset.id]: new Set() }));
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally {
      setRecomposingId(null);
    }
  };

  const handleRender = async () => {
    setRendering(true); setError(null);
    try {
      await renderLaneBrief(briefId, { tier: renderTier, lipsync: lipSync, captionsMode });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally {
      setRendering(false);
    }
  };

  const handleRejudge = async () => {
    setBusy(true);
    try { await rejudgeLaneBrief(briefId); await load(); onChanged?.(); }
    catch (e) { setError(String(e)); }
    finally { setBusy(false); }
  };

  const handleMarkLaunched = async () => {
    setBusy(true);
    try { await markLaneBriefLaunched(briefId); await load(); onChanged?.(); }
    catch (e) { setError(String(e)); }
    finally { setBusy(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this brief? This cannot be undone.')) return;
    setBusy(true);
    try { await deleteLaneBrief(briefId); onChanged?.(); onClose(); }
    catch (e) { setError(String(e)); }
    finally { setBusy(false); }
  };

  const startEdit = () => {
    setDraft(brief?.rendered_brief || '');
    setEditing(true);
  };
  const cancelEdit = () => { setEditing(false); setDraft(''); };
  const saveScript = async () => {
    setSavingScript(true); setError(null);
    try {
      await updateLaneBrief(briefId, { rendered_brief: draft });
      setEditing(false);
      setDraft('');
      await load();
      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally {
      setSavingScript(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h6">{brief?.brief_code || 'Brief'}</Typography>
          {brief && <BriefStatusChip status={brief.status} />}
          {brief?.judge_score != null && (
            <Chip size="small" label={`judge ${brief.judge_score.toFixed(2)}`} />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        {loading && <LinearProgress />}
        {error && <Alert severity="error">{error}</Alert>}
        {brief && (
          <Box>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Chip size="small" label={`Concept: ${brief.concept_name}`} />
              <Chip size="small" label={`Cell: ${brief.cell_name}`} />
              <Chip size="small" label={`Class: ${brief.concept_class}`} variant="outlined" />
              <Chip size="small" label={`Vertical: ${brief.vertical_label}`} variant="outlined" />
              <Chip size="small" label={brief.client_name} variant="outlined" />
            </Stack>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab value="rendered" label="Designer brief (markdown)" />
              <Tab
                value="designer"
                label={brief.task
                  ? `Designer (${brief.task.status_display || brief.task.status})`
                  : 'Designer / assign'}
              />
              <Tab value="shots" label="Shots" />
              <Tab value="json" label="Structured JSON" />
              <Tab value="judge" label={`Judge validation${brief.judge_validation ? '' : ' (none)'}`} />
              <Tab value="render" label={`AI render${renderedAssets.length ? ` (${renderedAssets.length})` : ''}`} />
            </Tabs>
            <Box sx={{ pt: 2 }}>
              {tab === 'rendered' && (
                <Stack spacing={1.5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                      {editing
                        ? 'Editing the master script — saving cascades live to every per-client task that uses this brief.'
                        : 'Read-only preview. Click Edit to revise.'}
                    </Typography>
                    {editing ? (
                      <>
                        <Button size="small" onClick={cancelEdit} disabled={savingScript}>
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={savingScript ? <CircularProgress size={14} /> : <SaveIcon />}
                          onClick={saveScript}
                          disabled={savingScript || draft.trim().length < 20}
                        >
                          {savingScript ? 'Saving…' : 'Save script'}
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="small" variant="outlined" startIcon={<EditIcon />}
                        onClick={startEdit}
                      >
                        Edit script
                      </Button>
                    )}
                  </Stack>
                  {editing ? (
                    <BriefMarkdownEditor
                      value={draft}
                      onChange={setDraft}
                      minRows={20}
                    />
                  ) : (
                    <Paper variant="outlined" sx={{ p: 2.5, maxHeight: 600, overflow: 'auto' }}>
                      <DesignerBriefView markdown={brief.rendered_brief} />
                    </Paper>
                  )}
                </Stack>
              )}
              {tab === 'designer' && (
                <AssignPanel
                  brief={brief}
                  busy={busy}
                  onAssigned={async () => { await load(); onChanged?.(); }}
                />
              )}
              {tab === 'shots' && (
                <ShotsPanel brief={brief} onChanged={onChanged} />
              )}
              {tab === 'json' && (
                <Paper variant="outlined" sx={{ p: 2, maxHeight: 600, overflow: 'auto', bgcolor: 'grey.50' }}>
                  <Typography component="pre" variant="caption" sx={{ whiteSpace: 'pre-wrap', m: 0, fontFamily: 'monospace' }}>
                    {JSON.stringify(brief.brief_json, null, 2)}
                  </Typography>
                </Paper>
              )}
              {tab === 'judge' && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  {brief.judge_validation ? (
                    <Stack spacing={1}>
                      <Typography variant="caption" fontWeight={600}>
                        Score: {brief.judge_score?.toFixed(2)}
                      </Typography>
                      {brief.judge_notes && (
                        <Alert severity="info">
                          <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
                            {brief.judge_notes}
                          </Typography>
                        </Alert>
                      )}
                      <Typography component="pre" variant="caption" sx={{ whiteSpace: 'pre-wrap', m: 0, fontFamily: 'monospace' }}>
                        {JSON.stringify(brief.judge_validation, null, 2)}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="caption">Not judged yet.</Typography>
                  )}
                </Paper>
              )}
              {tab === 'render' && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Runs the VideoRenderer pipeline: gpt-image-1 character image (cached on brief) +
                          Seedance image-to-video per shot + ElevenLabs VO + ffmpeg composition.
                          Async (5-15 min) — poll below. If this brief has no shot-list yet it's
                          expanded automatically first (one-time, creative locked).
                        </Typography>
                      </Box>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={renderTier}
                        onChange={(_, v) => v && setRenderTier(v)}
                        disabled={rendering}
                      >
                        <ToggleButton value="fast">
                          Fast 480p<br/><Typography variant="caption">~$2/render</Typography>
                        </ToggleButton>
                        <ToggleButton value="pro">
                          Pro 720p<br/><Typography variant="caption">~$7.50/render</Typography>
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <TextField
                        select
                        size="small"
                        label="Captions"
                        value={captionsMode}
                        onChange={(e) => setCaptionsMode(e.target.value)}
                        disabled={rendering}
                        sx={{ minWidth: 180 }}
                        helperText="Banger-style burned-in captions"
                      >
                        <MenuItem value="auto">Auto (Highlight)</MenuItem>
                        <MenuItem value="highlight">Highlight (3-word window, active word colored)</MenuItem>
                        <MenuItem value="word">Word-by-word (one colored word)</MenuItem>
                        <MenuItem value="keyword">Keyword (sparse phrases)</MenuItem>
                        <MenuItem value="off">Off</MenuItem>
                      </TextField>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={lipSync}
                            onChange={(e) => setLipSync(e.target.checked)}
                            disabled={rendering}
                          />
                        }
                        label={
                          <Typography variant="caption" color="text.secondary">
                            Lip-sync<br/>+~$6 (not recommended — glitchy on AI faces)
                          </Typography>
                        }
                      />
                      <Button
                        variant="contained"
                        startIcon={rendering ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
                        onClick={handleRender}
                        disabled={rendering}
                      >
                        {rendering ? 'Rendering…' : (renderedAssets.length ? 'Render again' : 'Render now')}
                      </Button>
                    </Stack>
                    {!brief?.brief_json?.shots?.length && (
                      <Alert severity="info">
                        No machine shot-list yet — the first render expands this brief
                        automatically (one Claude call, ~30s, creative stays locked),
                        then renders. Subsequent renders reuse it.
                      </Alert>
                    )}
                    {renderedAssets.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        No renders yet. Click "Render now" to produce the first MP4.
                      </Typography>
                    )}
                    {renderedAssets.map((asset) => (
                      <Card key={asset.id} variant="outlined">
                        <CardContent>
                          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                            <Chip
                              size="small"
                              label={asset.status}
                              color={
                                asset.status === 'completed' ? 'success'
                                : asset.status === 'failed' ? 'error'
                                : asset.status === 'rendering' ? 'warning'
                                : 'default'
                              }
                            />
                            <Typography variant="caption">
                              {asset.duration_seconds ? `${asset.duration_seconds}s · ` : ''}
                              {asset.aspect_ratio || '?'} ·
                              cost ${asset.cost_actual_usd?.toFixed?.(2) ?? '—'}
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <Typography variant="caption" color="text.secondary">
                              {asset.completed_at ? new Date(asset.completed_at).toLocaleString() : new Date(asset.created_at).toLocaleString()}
                            </Typography>
                          </Stack>
                          {asset.status === 'completed' && asset.output_url && (
                            <video src={asset.output_url} controls style={{ width: '100%', maxHeight: 500, background: '#000' }} />
                          )}
                          {asset.status === 'completed' && asset.shots?.length > 0 && (
                            <Box sx={{ mt: 1.5 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Per-shot — tick the ones to regenerate, then Recompose (reused shots cost $0):
                              </Typography>
                              <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1 }}>
                                {asset.shots.map((sh) => {
                                  const sel = (regenSel[asset.id] || new Set()).has?.(sh.shot_id);
                                  return (
                                    <Box key={sh.id} sx={{ minWidth: 150, maxWidth: 150, border: '1px solid', borderColor: sel ? 'warning.main' : 'divider', borderRadius: 1, p: 0.5 }}>
                                      {sh.clip_url ? (
                                        <video src={sh.clip_url} controls style={{ width: '100%', height: 90, objectFit: 'cover', background: '#000', borderRadius: 2 }} />
                                      ) : (
                                        <Box sx={{ height: 90, bgcolor: '#222', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                          <Typography variant="caption" color="text.disabled">no clip</Typography>
                                        </Box>
                                      )}
                                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mt: 0.5 }}>
                                        {sh.shot_id}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>
                                        {sh.source}{sh.has_native_speech ? ' · speech' : ''}{sh.cost_usd != null ? ` · $${Number(sh.cost_usd).toFixed(2)}` : ''}
                                      </Typography>
                                      {sh.vo_line && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: 10 }}>
                                          “{sh.vo_line}”
                                        </Typography>
                                      )}
                                      <FormControlLabel
                                        sx={{ m: 0 }}
                                        control={<Switch size="small" checked={!!sel} onChange={() => toggleRegen(asset.id, sh.shot_id)} />}
                                        label={<Typography variant="caption" sx={{ fontSize: 10 }}>regenerate</Typography>}
                                      />
                                    </Box>
                                  );
                                })}
                              </Stack>
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{ mt: 0.5 }}
                                disabled={recomposingId === asset.id}
                                startIcon={recomposingId === asset.id ? <CircularProgress size={12} /> : null}
                                onClick={() => handleRecompose(asset)}
                              >
                                {recomposingId === asset.id
                                  ? 'Recomposing…'
                                  : `Recompose (${(regenSel[asset.id]?.size) || 0} regen → ~$${(((regenSel[asset.id]?.size) || 0) * (renderTier === 'pro' ? 1.85 : 0.4) + 0.02).toFixed(2)})`}
                              </Button>
                            </Box>
                          )}
                          {asset.status === 'failed' && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                              <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', m: 0, fontFamily: 'monospace' }}>
                                {asset.error_log?.slice(0, 800)}
                              </Typography>
                            </Alert>
                          )}
                          {asset.adapters_used && Object.keys(asset.adapters_used).length > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                              Adapters: {Object.entries(asset.adapters_used).map(([k, v]) => `${k}=${v}`).join(', ')}
                            </Typography>
                          )}
                          {asset.render_log && (
                            <details style={{ marginTop: 8 }}>
                              <summary><Typography variant="caption">Render log</Typography></summary>
                              <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', m: 0, fontFamily: 'monospace', fontSize: 11, maxHeight: 200, overflow: 'auto' }}>
                                {asset.render_log}
                              </Typography>
                            </details>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </Paper>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />} disabled={busy}>
          Delete
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleRejudge} disabled={busy} startIcon={<RefreshIcon />}>
          Re-judge
        </Button>
        <Button onClick={handleMarkLaunched} disabled={busy || brief?.status === 'launched'} startIcon={<LaunchIcon />}>
          Mark launched
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// WINNERS TAB — banger library
// ---------------------------------------------------------------------------

function WinnersTab() {
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    listWinnerLibrary()
      .then((res) => setWinners(asArray(res)))
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {winners.length} banger reference(s). Each provides DNA the brief generator anchors on.
          Add new winners as you decompose them via Gemini.
        </Typography>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setShowAdd(true)}>
          Add winner
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {loading && <LinearProgress />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Winner</TableCell>
              <TableCell>Concept</TableCell>
              <TableCell>Vertical</TableCell>
              <TableCell>Cell</TableCell>
              <TableCell>Score</TableCell>
              <TableCell>COM</TableCell>
              <TableCell>Why it worked</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {winners.map((w) => (
              <TableRow key={w.id} hover>
                <TableCell><Typography variant="caption" fontWeight={600}>{w.name}</Typography></TableCell>
                <TableCell><Typography variant="caption">{w.concept_name}</Typography></TableCell>
                <TableCell><Typography variant="caption">{w.vertical_label}</Typography></TableCell>
                <TableCell><Typography variant="caption">{w.cell_name || '—'}</Typography></TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {w.creative_performance_score_at_peak != null
                      ? w.creative_performance_score_at_peak.toFixed(1) : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {w.com_at_peak != null ? `${(w.com_at_peak * 100).toFixed(1)}%` : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 360 }}>
                    {w.why_it_worked?.slice(0, 200)}{w.why_it_worked?.length > 200 ? '…' : ''}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(w)}><EditIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!winners.length && !loading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="caption" color="text.secondary">
                    No winners yet. Decompose a banger via Gemini and click "Add winner".
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {(showAdd || editing) && (
        <WinnerFormDialog
          open
          winner={editing}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          onSaved={() => { setShowAdd(false); setEditing(null); refresh(); }}
        />
      )}
    </Stack>
  );
}

function WinnerFormDialog({ open, winner, onClose, onSaved }) {
  const isEdit = !!winner;
  const [concepts, setConcepts] = useState([]);
  const [form, setForm] = useState({
    name: winner?.name || '',
    concept: winner?.concept || '',
    cell: winner?.cell || '',
    vertical: winner?.vertical || '',
    source_video_filename: winner?.source_video_filename || '',
    source_dropbox_url: winner?.source_dropbox_url || '',
    why_it_worked: winner?.why_it_worked || '',
    creative_performance_score_at_peak: winner?.creative_performance_score_at_peak ?? '',
    com_at_peak: winner?.com_at_peak ?? '',
    run_length_days: winner?.run_length_days ?? '',
    decomposition_text: winner?.decomposition ? JSON.stringify(winner.decomposition, null, 2) : '{\n  \n}',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    listAdConcepts().then((r) => setConcepts(asArray(r)));
  }, []);

  const handleField = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      let decomp;
      try { decomp = JSON.parse(form.decomposition_text || '{}'); }
      catch { throw new Error('Decomposition JSON is invalid'); }
      const payload = {
        name: form.name,
        concept: form.concept ? Number(form.concept) : null,
        cell: form.cell || null,
        vertical: form.vertical || null,
        source_video_filename: form.source_video_filename,
        source_dropbox_url: form.source_dropbox_url,
        why_it_worked: form.why_it_worked,
        decomposition: decomp,
        active: true,
      };
      if (form.creative_performance_score_at_peak !== '')
        payload.creative_performance_score_at_peak = Number(form.creative_performance_score_at_peak);
      if (form.com_at_peak !== '') payload.com_at_peak = Number(form.com_at_peak);
      if (form.run_length_days !== '') payload.run_length_days = Number(form.run_length_days);

      if (isEdit) await updateWinnerLibraryEntry(winner.id, payload);
      else await createWinnerLibraryEntry(payload);
      onSaved();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this winner?')) return;
    setSubmitting(true);
    try { await deleteWinnerLibraryEntry(winner.id); onSaved(); }
    catch (e) { setError(String(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit winner' : 'Add winner from Gemini decomposition'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Paste the full structured JSON from Gemini's Banger Decomposition Prompt into the
            "Decomposition JSON" field.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField label="Name" value={form.name} onChange={handleField('name')} fullWidth size="small" required />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select label="Concept"
                value={form.concept} onChange={handleField('concept')}
                fullWidth size="small" required
              >
                <MenuItem value="">—</MenuItem>
                {concepts.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField label="Vertical ID (optional)" value={form.vertical} onChange={handleField('vertical')} fullWidth size="small" />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Cell ID (optional)" value={form.cell} onChange={handleField('cell')} fullWidth size="small" />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Source video filename" value={form.source_video_filename} onChange={handleField('source_video_filename')} fullWidth size="small" />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Dropbox URL" value={form.source_dropbox_url} onChange={handleField('source_dropbox_url')} fullWidth size="small" />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Performance score at peak" type="number" value={form.creative_performance_score_at_peak} onChange={handleField('creative_performance_score_at_peak')} fullWidth size="small" />
            </Grid>
            <Grid item xs={4}>
              <TextField label="COM at peak (decimal)" type="number" value={form.com_at_peak} onChange={handleField('com_at_peak')} fullWidth size="small" helperText="e.g. 0.07 for 7%" />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Run length (days)" type="number" value={form.run_length_days} onChange={handleField('run_length_days')} fullWidth size="small" />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Why it worked" value={form.why_it_worked} onChange={handleField('why_it_worked')} multiline minRows={2} fullWidth size="small" />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Decomposition JSON"
                value={form.decomposition_text}
                onChange={handleField('decomposition_text')}
                multiline
                minRows={10}
                fullWidth
                size="small"
                sx={{ fontFamily: 'monospace' }}
              />
            </Grid>
          </Grid>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        {isEdit && <Button color="error" onClick={handleDelete} disabled={submitting}>Delete</Button>}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={submitting || !form.name || !form.concept}
          startIcon={submitting ? <CircularProgress size={14} /> : <SaveIcon />}
        >
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// CONSTITUTION TAB — view / edit sections
// ---------------------------------------------------------------------------

const CONSTITUTION_SECTIONS = [
  { key: 'voice_rules', label: '§1 — Voice rules' },
  { key: 'universal_production_rules', label: '§2 — Universal production rules' },
  { key: 'polished_class_rules', label: '§3a — Polished class rules' },
  { key: 'authenticity_class_rules', label: '§3b — Authenticity class rules' },
  { key: 'flight_composition_rule', label: '§4 — Flight composition rule' },
  { key: 'lead_quality_guardrails', label: '§5 — Lead quality guardrails' },
  { key: 'performance_metric_notes', label: '§7 — Performance metric notes' },
  { key: 'production_constraints', label: '§8 — Production constraints' },
  { key: 'escalation_rules', label: '§9 — Escalation rules' },
  { key: 'review_cadence', label: '§10 — Review cadence' },
  { key: 'banger_decomposition_prompt', label: 'Workflow — Banger decomposition prompt' },
];

// ---------------------------------------------------------------------------
// Add Banger tab — decomposition prompt + JSON paste form
// ---------------------------------------------------------------------------

// Enum option sets — kept in sync with the prompt and SubAxisCell schema.
const CLASS_OPTIONS = ['polished', 'authenticity'];
const AXIS_OPTIONS = {
  axis_talent: ['owner', 'installer', 'employee', 'customer', 'hands_only', 'faceless', 'animated', 'multiple'],
  axis_opening_3s: ['face_to_camera', 'motion_reveal', 'object_close_up', 'text_card', 'graphic_animation', 'pattern_interrupt', 'question_text', 'transformation_cut'],
  axis_format_pacing: ['single_take', 'jump_cut', 'slideshow', 'TH_plus_broll', 'screen_recording', 'animation', 'frenetic', 'ASMR_slow'],
  axis_audio_bed: ['sync_only', 'VO_over_broll', 'music_led_no_VO', 'music_plus_VO', 'silent_captioned', 'trending_audio', 'dramatic_announcer', 'whisper'],
  axis_narrative_arc: ['confession', 'reveal', 'walkthrough', 'announcement', 'demo', 'testimonial', 'comparison', 'explainer', 'conflict', 'countdown'],
  axis_caption_treatment: ['burned_in_bouncing', 'burned_in_chyron', 'burned_in_minimal', 'native_TikTok_style', 'branded_overlay'],
  estimated_cost_tier: ['low', 'medium', 'high'],
};
const AXIS_LABELS = {
  axis_talent: 'Talent',
  axis_opening_3s: 'Opening 3s shot',
  axis_format_pacing: 'Format / pacing',
  axis_audio_bed: 'Audio bed',
  axis_narrative_arc: 'Narrative arc',
  axis_caption_treatment: 'Captions',
  estimated_cost_tier: 'Cost tier',
};

// Paste an IG/FB ad link (or upload a video) → a background job fetches it and
// decomposes it with Gemini into the banger schema → "Use" drops the JSON into
// the Add Banger form below.
function AdInspirationPanel({ onUse }) {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    listAdInspirations()
      .then((r) => setItems(asArray(r)))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll while anything is still fetching/analyzing.
  useEffect(() => {
    const inFlight = items.some((i) => ['pending', 'fetching', 'analyzing'].includes(i.status));
    if (!inFlight) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [items, load]);

  const analyze = async () => {
    if (!url.trim()) return;
    setBusy(true); setErr(null);
    try {
      await createAdInspiration({ sourceUrl: url.trim(), title: title.trim() });
      setUrl(''); setTitle('');
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.response?.data?.source_url?.[0] || String(e));
    } finally {
      setBusy(false);
    }
  };

  const STATUS = {
    pending: ['Queued', 'default'], fetching: ['Fetching video', 'info'],
    analyzing: ['Analyzing', 'warning'], completed: ['Analyzed', 'success'],
    failed: ['Failed', 'error'],
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle2">Analyze a found ad</Typography>
          <Typography variant="caption" color="text.secondary">
            Paste an Instagram/Facebook ad link — we fetch the video and let Gemini
            break it down into the banger schema. Then “Use” it to pre-fill the form below.
          </Typography>
        </Box>
        {err && <Alert severity="error">{err}</Alert>}
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            size="small" fullWidth label="IG / FB ad link"
            placeholder="https://www.instagram.com/reel/…"
            value={url} onChange={(e) => setUrl(e.target.value)}
          />
          <TextField
            size="small" label="Label (optional)" sx={{ minWidth: 180 }}
            value={title} onChange={(e) => setTitle(e.target.value)}
          />
          <Button
            variant="contained" onClick={analyze}
            disabled={busy || !url.trim()}
            startIcon={busy ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Analyze
          </Button>
        </Stack>
        {items.length > 0 && (
          <Stack spacing={1}>
            {items.slice(0, 8).map((it) => {
              const [label, color] = STATUS[it.status] || STATUS.pending;
              return (
                <Box
                  key={it.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                        border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}
                >
                  <Chip size="small" label={label} color={color} />
                  <Typography variant="caption" sx={{ flex: 1, minWidth: 160, wordBreak: 'break-all' }}>
                    {it.title || it.source_url || `#${it.id}`}
                  </Typography>
                  {it.status === 'failed' && (
                    <>
                      <Typography variant="caption" color="error" sx={{ flexBasis: '100%' }}>
                        {it.error_log}
                      </Typography>
                      <Button size="small" onClick={() => reanalyzeAdInspiration(it.id).then(load)}>
                        Retry
                      </Button>
                    </>
                  )}
                  {it.status === 'completed' && (
                    <Button
                      size="small" variant="outlined"
                      onClick={() => onUse?.(it.decomposition)}
                    >
                      Use ↓
                    </Button>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

function AddBangerTab() {
  const [promptTemplate, setPromptTemplate] = useState('');
  const [concepts, setConcepts] = useState([]);
  const [loadingPrompt, setLoadingPrompt] = useState(true);
  const [error, setError] = useState(null);

  const [conceptChoice, setConceptChoice] = useState('AUTO');
  const [classChoice, setClassChoice] = useState('AUTO');

  const [jsonText, setJsonText] = useState('');
  const [parsed, setParsed] = useState(null);
  const [verticalSlug, setVerticalSlug] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [sourceFilename, setSourceFilename] = useState('');
  const [comAtPeak, setComAtPeak] = useState('');
  const [runLengthDays, setRunLengthDays] = useState('');
  const [perfScore, setPerfScore] = useState('');
  const [maturity, setMaturity] = useState('proven'); // proven | experimental

  const [submitting, setSubmitting] = useState(false);
  const [parseError, setParseError] = useState('');
  const [created, setCreated] = useState(null);

  // Auto-parse the JSON whenever the textarea changes — populates the Review form.
  useEffect(() => {
    const text = jsonText.trim();
    if (!text) {
      setParsed(null);
      setParseError('');
      return;
    }
    try {
      const obj = JSON.parse(text);
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
        setParsed(null);
        setParseError('Top-level JSON must be an object.');
        return;
      }
      setParsed(obj);
      setParseError('');
    } catch (e) {
      setParsed(null);
      setParseError(`Invalid JSON: ${e.message}`);
    }
  }, [jsonText]);

  const setTopField = (key, value) => {
    setParsed((p) => ({ ...(p || {}), [key]: value }));
  };
  const setCellField = (key, value) => {
    setParsed((p) => ({ ...(p || {}), cell: { ...((p || {}).cell || {}), [key]: value } }));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, cl] = await Promise.all([
          fetchActiveConstitution(),
          listAdConcepts(),
        ]);
        if (cancelled) return;
        setPromptTemplate(unwrap(c)?.banger_decomposition_prompt || '');
        setConcepts(asArray(cl));
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e?.message || 'Failed to load prompt.');
      } finally {
        if (!cancelled) setLoadingPrompt(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const renderedPrompt = useMemo(() => (
    (promptTemplate || '')
      .replace(/\{\{concept_choice\}\}/g, conceptChoice || 'AUTO')
      .replace(/\{\{class_choice\}\}/g, classChoice || 'AUTO')
  ), [promptTemplate, conceptChoice, classChoice]);

  const copyPrompt = () => {
    if (!renderedPrompt) return;
    navigator.clipboard.writeText(renderedPrompt);
  };

  const submit = async () => {
    setError(null);
    setCreated(null);
    if (!parsed) {
      setParseError(parseError || 'Paste the decomposition JSON first.');
      return;
    }
    const payload = { ...parsed };
    if (conceptChoice && conceptChoice !== 'AUTO') payload.concept = conceptChoice;
    if (classChoice && classChoice !== 'AUTO') payload.class = classChoice;
    if (verticalSlug) payload.vertical_slug = verticalSlug;
    if (sourceUrl) payload.source_dropbox_url = sourceUrl;
    if (sourceFilename) payload.source_video_filename = sourceFilename;
    if (comAtPeak) payload.com_at_peak = Number(comAtPeak);
    if (runLengthDays) payload.run_length_days = Number(runLengthDays);
    if (perfScore) payload.creative_performance_score_at_peak = Number(perfScore);
    payload.maturity = maturity;

    setSubmitting(true);
    try {
      const res = await addBangerFromDecomposition(payload);
      setCreated(unwrap(res));
      setJsonText('');
      setParsed(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Save failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6">Add a new banger</Typography>
        <Typography variant="body2" color="text.secondary">
          Paste the prompt below into Gemini (or any video-capable LLM) along with the banger
          video. Paste the JSON it returns here. We&apos;ll create the WinnerLibrary entry and a new
          canonical SubAxisCell anchored on it.
        </Typography>
      </Box>

      <AdInspirationPanel onUse={(decomp) => {
        setJsonText(JSON.stringify(decomp || {}, null, 2));
        // An ad found in the wild has no track record for us → experimental.
        setMaturity('experimental');
      }} />

      {error && <Alert severity="error">{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">Banger Decomposition Prompt</Typography>
            <Button size="small" onClick={copyPrompt} disabled={!renderedPrompt}>Copy</Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            If you already know the concept / class, lock them in here and the prompt updates so
            Gemini can&apos;t pick a different one. Leave on AUTO to let Gemini decide.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Lock concept"
                value={conceptChoice}
                onChange={(e) => setConceptChoice(e.target.value)}
              >
                <MenuItem value="AUTO"><em>AUTO — let Gemini pick</em></MenuItem>
                {concepts.map((c) => (
                  <MenuItem key={c.slug} value={c.slug}>{c.name} ({c.slug})</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Lock class"
                value={classChoice}
                onChange={(e) => setClassChoice(e.target.value)}
              >
                <MenuItem value="AUTO"><em>AUTO — let Gemini pick</em></MenuItem>
                <MenuItem value="polished">polished</MenuItem>
                <MenuItem value="authenticity">authenticity</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Maturity"
                value={maturity}
                onChange={(e) => setMaturity(e.target.value)}
                helperText={maturity === 'experimental'
                  ? 'Untested angle — kept segmented from proven cells until it earns data'
                  : 'Has a track record'}
              >
                <MenuItem value="proven">Proven (has a track record)</MenuItem>
                <MenuItem value="experimental">🧪 Experimental (untested angle)</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          {loadingPrompt ? (
            <CircularProgress size={20} />
          ) : (
            <TextField
              multiline
              fullWidth
              minRows={8}
              maxRows={16}
              value={renderedPrompt}
              InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            Edit the underlying template in the Constitution tab as you learn what additional axes matter.
          </Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="subtitle2">Paste decomposition JSON</Typography>
          <TextField
            multiline
            fullWidth
            minRows={10}
            maxRows={24}
            placeholder='{ "name": "...", "concept": "vsl", "cell": { ... }, ... }'
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
            error={Boolean(parseError)}
            helperText={parseError || (parsed ? '✓ Parsed — review fields below before saving' : ' ')}
          />

          {parsed && (
            <>
              <Divider>
                <Typography variant="caption" color="text.secondary">Review &amp; correct</Typography>
              </Divider>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Banger name"
                    value={parsed.name || ''}
                    onChange={(e) => setTopField('name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    fullWidth
                    label="Concept"
                    value={parsed.concept || ''}
                    onChange={(e) => setTopField('concept', e.target.value)}
                  >
                    {concepts.map((c) => (
                      <MenuItem key={c.slug} value={c.slug}>{c.name}</MenuItem>
                    ))}
                    {parsed.concept && !concepts.some((c) => c.slug === parsed.concept) && (
                      <MenuItem value={parsed.concept}>{parsed.concept} (unknown)</MenuItem>
                    )}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    fullWidth
                    label="Class"
                    value={parsed.class || ''}
                    onChange={(e) => setTopField('class', e.target.value)}
                  >
                    {CLASS_OPTIONS.map((v) => (
                      <MenuItem key={v} value={v}>{v}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Cell name"
                    value={parsed.cell?.name || ''}
                    onChange={(e) => setCellField('name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Setting (free text)"
                    value={parsed.cell?.axis_setting || ''}
                    onChange={(e) => setCellField('axis_setting', e.target.value)}
                  />
                </Grid>

                {Object.keys(AXIS_OPTIONS).map((key) => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <TextField
                      select
                      fullWidth
                      label={AXIS_LABELS[key]}
                      value={parsed.cell?.[key] || ''}
                      onChange={(e) => setCellField(key, e.target.value)}
                    >
                      <MenuItem value=""><em>(unset)</em></MenuItem>
                      {AXIS_OPTIONS[key].map((v) => (
                        <MenuItem key={v} value={v}>{v}</MenuItem>
                      ))}
                      {parsed.cell?.[key] && !AXIS_OPTIONS[key].includes(parsed.cell[key]) && (
                        <MenuItem value={parsed.cell[key]}>{parsed.cell[key]} (out of enum)</MenuItem>
                      )}
                    </TextField>
                  </Grid>
                ))}

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Talent specifics"
                    value={parsed.cell?.axis_talent_specifics || ''}
                    onChange={(e) => setCellField('axis_talent_specifics', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Audio specifics"
                    value={parsed.cell?.axis_audio_specifics || ''}
                    onChange={(e) => setCellField('axis_audio_specifics', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Opening 3s description"
                    value={parsed.cell?.axis_opening_3s_description || ''}
                    onChange={(e) => setCellField('axis_opening_3s_description', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Length min (s)"
                    value={parsed.cell?.target_length_seconds_min ?? ''}
                    onChange={(e) => setCellField('target_length_seconds_min', e.target.value ? Number(e.target.value) : null)}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Length max (s)"
                    value={parsed.cell?.target_length_seconds_max ?? ''}
                    onChange={(e) => setCellField('target_length_seconds_max', e.target.value ? Number(e.target.value) : null)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Hook pattern"
                    value={parsed.cell?.hook_pattern || ''}
                    onChange={(e) => setCellField('hook_pattern', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="What makes it work"
                    value={parsed.cell?.what_makes_it_work || ''}
                    onChange={(e) => setCellField('what_makes_it_work', e.target.value)}
                  />
                </Grid>
              </Grid>
            </>
          )}

          <Divider />
          <Typography variant="subtitle2">Context (optional)</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Vertical slug"
                placeholder="bath / windows / roofing / flooring / siding"
                value={verticalSlug}
                onChange={(e) => setVerticalSlug(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Source video URL (Dropbox/Drive)"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Source filename"
                value={sourceFilename}
                onChange={(e) => setSourceFilename(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="COM at peak (e.g. 0.07)"
                value={comAtPeak}
                onChange={(e) => setComAtPeak(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Run length (days)"
                value={runLengthDays}
                onChange={(e) => setRunLengthDays(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Perf score at peak"
                value={perfScore}
                onChange={(e) => setPerfScore(e.target.value)}
              />
            </Grid>
          </Grid>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={submit}
              disabled={submitting || !parsed}
              startIcon={submitting ? <CircularProgress size={16} /> : <AddIcon />}
            >
              {submitting ? 'Saving…' : 'Create banger'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {created && (
        <Alert severity="success">
          Created <strong>{created.name}</strong> (concept: {created.concept_name || created.concept})
          {created.cell_name ? <> → new canonical cell <strong>{created.cell_name}</strong></> : null}.
          Visible now under the Winner Library tab.
        </Alert>
      )}
    </Stack>
  );
}


function ConstitutionTab() {
  const [constitution, setConstitution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetchActiveConstitution()
      .then((res) => { const c = unwrap(res); setConstitution(c); setDraft(c); })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => {
    if (!constitution) return false;
    return CONSTITUTION_SECTIONS.some((s) => draft[s.key] !== constitution[s.key])
      || JSON.stringify(draft.vertical_priors || {}) !== JSON.stringify(constitution.vertical_priors || {})
      || JSON.stringify(draft.target_ranges || {}) !== JSON.stringify(constitution.target_ranges || {});
  }, [constitution, draft]);

  const save = useCallback(async () => {
    setSaving(true); setError(null);
    try {
      const payload = {};
      CONSTITUTION_SECTIONS.forEach((s) => { payload[s.key] = draft[s.key] || ''; });
      // JSON sections
      try {
        if (typeof draft.vertical_priors_text === 'string') {
          payload.vertical_priors = JSON.parse(draft.vertical_priors_text);
        } else payload.vertical_priors = draft.vertical_priors;
      } catch { throw new Error('Vertical priors JSON is invalid'); }
      try {
        if (typeof draft.target_ranges_text === 'string') {
          payload.target_ranges = JSON.parse(draft.target_ranges_text);
        } else payload.target_ranges = draft.target_ranges;
      } catch { throw new Error('Target ranges JSON is invalid'); }

      const updated = unwrap(await updateConstitution(constitution.id, payload));
      setConstitution(updated);
      setDraft(updated);
      setSavedAt(new Date());
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  }, [constitution, draft]);

  if (loading) return <LinearProgress />;
  if (error && !constitution) return <Alert severity="error">{error}</Alert>;
  if (!constitution) return <Alert severity="warning">No active constitution found.</Alert>;

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Active: <strong>{constitution.name}</strong> · Updated {new Date(constitution.updated_at).toLocaleString()}
          {savedAt && ` · Saved ${savedAt.toLocaleTimeString()}`}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AutoFixHighIcon />}
          onClick={() => setAuditOpen(true)}
        >
          Audit against bangers
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
          onClick={save}
          disabled={!dirty || saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Stack>

      {auditOpen && (
        <AuditConstitutionDialog
          constitution={constitution}
          onClose={() => setAuditOpen(false)}
          onApplied={() => { setAuditOpen(false); load(); }}
        />
      )}
      {error && <Alert severity="error">{error}</Alert>}

      {CONSTITUTION_SECTIONS.map((s) => (
        <TextField
          key={s.key}
          label={s.label}
          value={draft[s.key] || ''}
          onChange={(e) => setDraft((d) => ({ ...d, [s.key]: e.target.value }))}
          multiline
          minRows={3}
          fullWidth
          size="small"
        />
      ))}

      <TextField
        label="§6 — Vertical priors (JSON)"
        value={
          typeof draft.vertical_priors_text === 'string'
            ? draft.vertical_priors_text
            : JSON.stringify(draft.vertical_priors || {}, null, 2)
        }
        onChange={(e) => setDraft((d) => ({ ...d, vertical_priors_text: e.target.value }))}
        multiline
        minRows={8}
        fullWidth
        size="small"
        sx={{ fontFamily: 'monospace' }}
      />
      <TextField
        label="§7 — Target ranges (JSON)"
        value={
          typeof draft.target_ranges_text === 'string'
            ? draft.target_ranges_text
            : JSON.stringify(draft.target_ranges || {}, null, 2)
        }
        onChange={(e) => setDraft((d) => ({ ...d, target_ranges_text: e.target.value }))}
        multiline
        minRows={6}
        fullWidth
        size="small"
        sx={{ fontFamily: 'monospace' }}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// AUDIT CONSTITUTION dialog
// ---------------------------------------------------------------------------

const VERDICT_COLOR = { holds: 'success', soft_warning: 'warning', weak: 'error', no_data: 'default' };

function AuditConstitutionDialog({ constitution, onClose, onApplied }) {
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [proposeEdits, setProposeEdits] = useState(true);
  const [appliedKeys, setAppliedKeys] = useState(new Set());
  const [applyingKey, setApplyingKey] = useState(null);

  const runAudit = useCallback(async () => {
    setRunning(true); setError(null); setReport(null); setAppliedKeys(new Set());
    try {
      const r = await auditConstitution(constitution.id, { propose_edits: proposeEdits });
      setReport(unwrap(r));
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally {
      setRunning(false);
    }
  }, [constitution.id, proposeEdits]);

  const apply = useCallback(async (edit) => {
    setApplyingKey(edit.section_key); setError(null);
    try {
      await applyConstitutionEdit(constitution.id, {
        section_key: edit.section_key,
        proposed_text: edit.proposed_text,
      });
      setAppliedKeys((s) => new Set([...s, edit.section_key]));
      onApplied?.();
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally {
      setApplyingKey(null);
    }
  }, [constitution.id, onApplied]);

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Audit Constitution against Winner Library</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Analyzes every active banger against each universal rule. Surfaces compliance rates,
            emerging patterns, and (optionally) calls Claude to propose Constitution text revisions.
            <strong>Costs ~$0.30 if propose_edits is on.</strong> Reliable confidence kicks in around 15+ bangers.
          </Alert>

          {!report && (
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={proposeEdits}
                    onChange={(e) => setProposeEdits(e.target.checked)}
                  />
                }
                label="Use Claude to propose text revisions"
              />
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                startIcon={running ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
                onClick={runAudit}
                disabled={running}
              >
                {running ? 'Auditing…' : 'Run audit'}
              </Button>
            </Stack>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {report && (
            <Stack spacing={2}>
              <Alert severity={report.has_enough_data ? 'success' : 'warning'}>
                {report.summary}
              </Alert>

              {/* Rule compliance table */}
              <Typography variant="subtitle2">Rule compliance ({report.bangers_analyzed} bangers analyzed)</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rule</TableCell>
                      <TableCell>Section</TableCell>
                      <TableCell align="right">Compliance</TableCell>
                      <TableCell>Verdict</TableCell>
                      <TableCell>Violators</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(report.rule_compliance || []).map((r) => (
                      <TableRow key={r.rule_id}>
                        <TableCell><Typography variant="caption">{r.rule_description}</Typography></TableCell>
                        <TableCell><Typography variant="caption" color="text.secondary">{r.section_key}</Typography></TableCell>
                        <TableCell align="right">
                          <Typography variant="caption">
                            {r.compliance_count}/{r.total_count} ({Math.round((r.compliance_rate || 0) * 100)}%)
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={r.verdict} color={VERDICT_COLOR[r.verdict] || 'default'} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {(r.violators || []).slice(0, 2).map((v) => v.banger_name).join(', ')}
                            {r.violators?.length > 2 ? `… +${r.violators.length - 2}` : ''}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Emerging patterns */}
              {(report.emerging_patterns || []).length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>Emerging patterns</Typography>
                  <Stack spacing={1}>
                    {report.emerging_patterns.map((p, i) => (
                      <Card key={i} variant="outlined">
                        <CardContent sx={{ py: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip size="small" label={`${p.axis_or_field}: ${p.pattern_value}`} />
                            <Typography variant="caption" color="text.secondary">
                              {p.occurrences}/{p.total} bangers ({Math.round((p.ratio || 0) * 100)}%)
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                              → suggests update to <strong>{p.section_key}</strong>
                            </Typography>
                          </Stack>
                          {p.notes && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{p.notes}</Typography>}
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </>
              )}

              {/* Contradictions */}
              {(report.contradictions || []).length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>High-performer contradictions</Typography>
                  <Stack spacing={1}>
                    {report.contradictions.map((c, i) => (
                      <Alert key={i} severity="warning" variant="outlined">
                        <Typography variant="caption">
                          <strong>{c.banger_name}</strong> (score {c.creative_performance_score_at_peak})
                          violates rule "<em>{c.rule_description}</em>" — observed: {c.observed_value}
                        </Typography>
                      </Alert>
                    ))}
                  </Stack>
                </>
              )}

              {/* Proposed edits */}
              {(report.proposed_edits || []).length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>Proposed Constitution edits</Typography>
                  <Stack spacing={1.5}>
                    {report.proposed_edits.map((edit, i) => {
                      const applied = appliedKeys.has(edit.section_key);
                      const applying = applyingKey === edit.section_key;
                      return (
                        <Card key={i} variant="outlined">
                          <CardContent>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                              <Chip size="small" label={edit.section_key} />
                              <Typography variant="caption">
                                Confidence: {Math.round((edit.confidence || 0) * 100)}%
                              </Typography>
                              <Box sx={{ flex: 1 }} />
                              {applied ? (
                                <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Applied" />
                              ) : (
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={applying ? <CircularProgress size={14} /> : <CheckCircleIcon />}
                                  onClick={() => apply(edit)}
                                  disabled={applying}
                                >
                                  {applying ? 'Applying…' : 'Apply'}
                                </Button>
                              )}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              <strong>Rationale:</strong> {edit.rationale}
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Typography variant="caption" fontWeight={600}>Current</Typography>
                                <Paper variant="outlined" sx={{ p: 1, maxHeight: 200, overflow: 'auto', bgcolor: 'grey.50' }}>
                                  <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
                                    {edit.current_text || '(empty)'}
                                  </Typography>
                                </Paper>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" fontWeight={600}>Proposed</Typography>
                                <Paper variant="outlined" sx={{ p: 1, maxHeight: 200, overflow: 'auto', bgcolor: 'success.lighter' }}>
                                  <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
                                    {edit.proposed_text}
                                  </Typography>
                                </Paper>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                </>
              )}

              <Stack direction="row" justifyContent="flex-end">
                <Button onClick={runAudit} disabled={running} startIcon={<RefreshIcon />}>
                  Re-run audit
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
